import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box, Typography, Grid, Chip, Avatar, Button, Divider, Stack,
  Tabs, Tab, CircularProgress, Pagination, Snackbar, Alert,
  Dialog, DialogContent, Radio, RadioGroup, FormControlLabel, TextField, IconButton, Rating,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LaptopIcon from '@mui/icons-material/Laptop';
import HomeIcon from '@mui/icons-material/Home';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { getProviderStats, getProviderBookings, getProviderCalendar, updateBookingStatus, completeBooking, getClientStats, getClientBookings, cancelClientBooking, solicitarCambio, responderCambio, getBookedSlots, addExtra, removeExtra, pagarExtras, reportarProblema, crearAclaracion } from '../../../actions/bookings';
import { getPagoBooking, verificarPagoBooking } from '../../../actions/payments';
import { getMisTickets } from '../../../actions/tickets';
import { startChat } from '../../../actions/chat';
import { submitReview } from '../../../actions/reviews';
import { getPublicServiceDetail } from '../../../actions/profile';
import { urlImg } from '../../../components/common/helpers';
import BookingCalendar from '../../../components/booking/BookingCalendar';
import BookingSchedule from '../../../components/booking/BookingSchedule';
import ReporteServicioModal from '../../../components/booking/ReporteServicioModal';
import DetalleReservaModal from '../../../components/booking/DetalleReservaModal';

// Botones de descarga de la factura (CFDI): aparecen en el historial cuando el
// superadmin ya subió el PDF y el XML. Reutilizado por ambas tarjetas.
const FacturaBotones = ({ factura }) => (
  <>
    {factura?.xml?.url && (
      <Button size="small" variant="contained" disableElevation
        component="a" href={factura.xml.url} target="_blank" rel="noopener noreferrer"
        sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#59C39A', color: 'white', fontSize: '12px', minWidth: 0, px: 2,
          '&:hover': { bgcolor: '#4aad87' } }}>
        XML
      </Button>
    )}
    {factura?.pdf?.url && (
      <Button size="small" variant="contained" disableElevation
        component="a" href={factura.pdf.url} target="_blank" rel="noopener noreferrer"
        sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#7495F8', color: 'white', fontSize: '12px', minWidth: 0, px: 2,
          '&:hover': { bgcolor: '#5f82e8' } }}>
        PDF
      </Button>
    )}
  </>
);

const STATUS_COLOR = {
  pendiente:  { bgcolor: '#FFD074', color: '#92400E', label: 'Pendiente' },
  confirmada: { bgcolor: '#9DE1D5', color: '#166534', label: 'Confirmado' },
  cancelada:  { bgcolor: '#FEE2E2', color: '#991B1B', label: 'Cancelado' },
  completada: { bgcolor: '#E0E7FF', color: '#3730A3', label: 'Completado' },
  expirada:   { bgcolor: '#F3F4F6', color: '#6B7280', label: 'Caducado' }
};

const minutesToLabel = (timeStr, extraMin = 0) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + (m || 0) + extraMin;
  const hh = Math.floor(total / 60) % 24;
  const mm = total % 60;
  const ampm = hh < 12 ? 'AM' : 'PM';
  const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
  return `${String(h12).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${ampm}`;
};

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  return `${day} de ${MONTH_NAMES[month - 1]}, ${year}`;
};

const formatCompactNumber = (n) =>
  new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(n ?? 0);

const formatCompactCurrency = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', notation: 'compact', maximumFractionDigits: 1 }).format(n ?? 0);

const CONTRATADOS_STATS = [
  { key: 'pendiente',  label: 'Pendientes',  color: '#F59E0B' },
  { key: 'activo',     label: 'Activos',     color: '#59C39A' },
  { key: 'proximo',    label: 'Próximos',    color: '#0E2465' },
  { key: 'finalizado', label: 'Finalizados', color: '#7495f8' },
  { key: 'caducado',   label: 'Caducos',     color: '#737791' },
  { key: 'cancelado',  label: 'Cancelados',  color: '#FF827A' },
];

const MOTIVOS = [
  { value: 'disponibilidad', label: 'Falta de disponibilidad', desc: 'Requiere más tiempo del solicitado.', emoji: '🔑' },
  { value: 'ubicacion',      label: 'Ubicación',               desc: 'Está fuera de mi radio de cobertura.', emoji: '📍' },
  { value: 'recursos',       label: 'Recursos',                desc: 'No cuento con el equipo necesario.', emoji: '🔧' },
  { value: 'otra',           label: 'Otra',                    desc: 'Cuéntanos por qué', emoji: '🚫' },
];

const DeclineModal = ({ open, onClose, onConfirm, loading }) => {
  const [razon, setRazon] = useState('disponibilidad');
  const [textoOtra, setTextoOtra] = useState('');

  const handleConfirm = () => {
    const motivo = razon === 'otra'
      ? textoOtra.trim()
      : MOTIVOS.find(m => m.value === razon)?.label || razon;
    onConfirm(motivo);
  };

  const handleClose = () => {
    setRazon('disponibilidad');
    setTextoOtra('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 2 } } }}>
      <DialogContent>
        {/* Ícono */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#FFD074', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WarningAmberIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
        </Box>

        <Typography variant="h5" fontWeight="900" color="#0E2465" textAlign="center" sx={{ mb: 1.5 }}>
          Estás por declinar un servicio
        </Typography>
        <Typography variant="body2" color="#737791" textAlign="center" sx={{ mb: 3, lineHeight: 1.6 }}>
          Lo cual significa que no podrás brindarlo al usuario,<br />
          hazle saber el por qué seleccionando la opción más adecuada
        </Typography>

        {razon !== 'otra' ? (
          <RadioGroup value={razon} onChange={e => setRazon(e.target.value)}>
            {MOTIVOS.map(m => (
              <Box key={m.value} onClick={() => setRazon(m.value)}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, px: 1, borderRadius: '8px', cursor: 'pointer',
                  '&:hover': { bgcolor: '#F8FAFC' } }}>
                <Radio value={m.value} size="small" sx={{ color: '#0E2465', '&.Mui-checked': { color: '#0E2465' }, pt: 0 }} />
                <Box>
                  <Typography variant="body2" fontWeight="bold" color="#0E2465">
                    {m.emoji} {m.label}
                    {m.value === 'otra' && <Typography component="span" variant="body2" color="#FF827A"> (Mensaje o nota para el usuario)</Typography>}
                  </Typography>
                  <Typography variant="caption" color="#737791">({m.desc})</Typography>
                </Box>
              </Box>
            ))}
          </RadioGroup>
        ) : (
          <Box sx={{ border: '1px solid #F1F5F9', borderRadius: '12px', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" fontWeight="bold" color="#0E2465">
                🚫 <Typography component="span" color="#FF827A">Otra (Mensaje o nota para el usuario)</Typography>
              </Typography>
            </Box>
            <Typography variant="caption" color="#737791" sx={{ display: 'block', mb: 1.5 }}>
              Hazle saber al usuario el motivo de la declinación, tal vez puedan llegar a un acuerdo
            </Typography>
            <TextField
              multiline rows={4} fullWidth
              placeholder="Ej. Tengo un servicio del otro lado de la ciudad antes y es posible que no llegue a tiempo..."
              value={textoOtra}
              onChange={e => setTextoOtra(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }}
            />
            <Button size="small" onClick={() => setRazon('disponibilidad')}
              sx={{ mt: 1, textTransform: 'none', color: '#737791', fontSize: '0.75rem' }}>
              ← Volver a opciones
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button fullWidth variant="contained" onClick={handleClose}
            sx={{ bgcolor: '#0E2465', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&:hover': { bgcolor: '#0a1a4a' } }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" onClick={handleConfirm} disabled={loading || (razon === 'otra' && !textoOtra.trim())}
            sx={{ bgcolor: '#FF827A', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&:hover': { bgcolor: '#e06f68' }, '&.Mui-disabled': { bgcolor: '#fca5a5' } }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Declinar servicio'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const getPagosStats = (stats) => [
  { key: 'recibir', label: 'Completados este mes',      color: '#22C55E', value: stats.recibir ?? 0, esMoneda: true },
  { key: 'cobrar',  label: 'Por cobrar (confirmados)',  color: '#F59E0B', value: stats.cobrar  ?? 0, esMoneda: true },
  { key: 'total',   label: 'Servicios completados mes', color: '#7495f8', value: stats.totalMes ?? 0, esMoneda: false },
];

const toFechaStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// directo: el cambio se aplica sin aprobación (cliente en reserva aún pendiente).
// esProveedor: quien modifica es el proveedor y la propuesta la aprueba el cliente.
const ModificarModal = ({ open, onClose, onConfirm, loading, booking, directo = false, esProveedor = false }) => {
  const [service, setService] = useState(null);
  const [loadingService, setLoadingService] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);

  // Cargar disponibilidad del servicio al abrir
  useEffect(() => {
    if (!open) return;
    setSelectedDate(null);
    setSelectedTime(null);
    setBookedSlots([]);
    const proveedorId = booking.proveedor?._id || booking.proveedor;
    if (!proveedorId || !booking.servicio) return;
    setLoadingService(true);
    getPublicServiceDetail(proveedorId, booking.servicio)
      .then(res => setService(res?.service || null))
      .finally(() => setLoadingService(false));
  }, [open, booking.proveedor, booking.servicio]);

  // Cargar slots ocupados al cambiar de fecha
  useEffect(() => {
    if (!selectedDate || !booking.servicio) return;
    setSelectedTime(null);
    getBookedSlots(booking.servicio, toFechaStr(selectedDate)).then(setBookedSlots);
  }, [selectedDate, booking.servicio]);

  const handleClose = () => { setSelectedDate(null); setSelectedTime(null); onClose(); };

  const fechaStr = selectedDate ? toFechaStr(selectedDate) : '';
  const isValid = fechaStr && selectedTime && (fechaStr !== booking.fecha || selectedTime !== booking.hora);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 2 } } }}>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#FFD074', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CalendarTodayIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
        </Box>

        <Typography variant="h5" fontWeight="900" color="#0E2465" textAlign="center" sx={{ mb: 1 }}>
          {directo ? 'Modificar fecha de la reserva' : esProveedor ? 'Proponer cambio de fecha' : 'Solicitar cambio de fecha'}
        </Typography>
        <Typography variant="body2" color="#737791" textAlign="center" sx={{ mb: 3, lineHeight: 1.6 }}>
          {directo ? (
            <>Elige una nueva fecha y horario dentro de la disponibilidad del proveedor.<br />
            Como aún no acepta tu solicitud, el cambio se aplica de inmediato y se le avisa.</>
          ) : esProveedor ? (
            <>Elige una nueva fecha y horario dentro de tu disponibilidad.<br />
            El cliente recibirá tu propuesta y deberá aceptarla.
            {booking.status === 'pendiente' && <> Si la acepta, la reserva quedará confirmada en automático con la nueva fecha.</>}</>
          ) : (
            <>Elige una nueva fecha y horario dentro de la disponibilidad del proveedor.<br />
            El proveedor recibirá tu solicitud y deberá aprobarla.</>
          )}
        </Typography>

        <Box sx={{ bgcolor: '#F1F5F9', borderRadius: '12px', p: 2, mb: 3 }}>
          <Typography variant="caption" color="#737791" sx={{ display: 'block', mb: 0.5 }}>Fecha y hora actuales</Typography>
          <Typography variant="body2" fontWeight="bold" color="#0E2465">
            {formatDate(booking.fecha)} — {minutesToLabel(booking.hora)}
          </Typography>
        </Box>

        {loadingService ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress sx={{ color: '#7495f8' }} />
          </Box>
        ) : (
          <Box sx={{ border: '1px solid #F1F5F9', borderRadius: '16px', p: 3, mb: 4 }}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <BookingCalendar
                  selectedDate={selectedDate}
                  onDateSelect={setSelectedDate}
                  hideContainer
                  disponibilidad={service?.disponibilidad}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {selectedDate ? (
                  <BookingSchedule
                    selectedTime={selectedTime}
                    onTimeSelect={setSelectedTime}
                    hideContainer
                    service={service}
                    selectedDate={selectedDate}
                    bookedSlots={bookedSlots}
                  />
                ) : (
                  <Typography variant="body2" color="#94A3B8" sx={{ mt: 1 }}>
                    Selecciona primero una fecha disponible.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={handleClose}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: '#E2E8F0', color: '#737791' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" onClick={() => onConfirm(booking._id, fechaStr, selectedTime)}
            disabled={loading || !isValid}
            sx={{ bgcolor: '#FFD074', color: '#92400E', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&:hover': { bgcolor: '#f0c35a' }, '&.Mui-disabled': { bgcolor: '#FFF3CD', color: '#d4a017' } }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : directo ? 'Guardar cambio' : esProveedor ? 'Enviar propuesta' : 'Enviar solicitud'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const AcceptModal = ({ open, onClose, onConfirm, loading, booking }) => {
  const { servicioNombre, fecha, hora, duracionMinutos, modalidad, cliente } = booking;
  const clientName = cliente ? `${cliente.name} ${cliente.apellido}` : 'Cliente';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 2 } } }}>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#59C39A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
        </Box>

        <Typography variant="h5" fontWeight="900" color="#0E2465" textAlign="center" sx={{ mb: 1 }}>
          Confirmar reserva
        </Typography>
        <Typography variant="body2" color="#737791" textAlign="center" sx={{ mb: 3, lineHeight: 1.6 }}>
          Estás a punto de comprometerte con este servicio.<br />
          El cliente será notificado de inmediato.
        </Typography>

        <Box sx={{ bgcolor: '#F1F5F9', borderRadius: '12px', p: 2.5, mb: 4 }}>
          <Typography variant="caption" fontWeight="bold" color="#737791" sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Resumen
          </Typography>
          <Stack spacing={1.2}>
            {[
              { label: 'Cliente',    value: clientName },
              { label: 'Servicio',   value: servicioNombre },
              { label: 'Fecha',      value: formatDate(fecha) },
              { label: 'Horario',    value: `${minutesToLabel(hora)} — ${minutesToLabel(hora, duracionMinutos)}` },
              { label: 'Modalidad',  value: modalidad },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: 'flex', gap: 1.5 }}>
                <Typography variant="caption" color="#737791" sx={{ minWidth: 64 }}>{label}:</Typography>
                <Typography variant="caption" fontWeight="bold" color="#0E2465">{value}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: '#E2E8F0', color: '#737791' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" onClick={onConfirm} disabled={loading}
            sx={{ bgcolor: '#59C39A', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&:hover': { bgcolor: '#4aad87' }, '&.Mui-disabled': { bgcolor: '#9DE1D5' } }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Confirmar reserva'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const CompletarModal = ({ open, onClose, onConfirm, loading, booking }) => {
  const [pin, setPin] = useState('');
  useEffect(() => { if (open) setPin(''); }, [open]);
  const totalExtras = Math.round((booking.extras || []).reduce((a, e) => a + e.monto, 0) * 100) / 100;
  // Los extras los paga el cliente desde su reserva ANTES de poder completar
  const extrasRestante = Math.round((totalExtras - (booking.pagoExtra?.pagado || 0)) * 100) / 100;
  const extrasSinPagar = !!booking.pago && extrasRestante > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#59C39A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
        </Box>
        <Typography variant="h6" fontWeight="900" color="#0E2465" textAlign="center" sx={{ mb: 1 }}>
          ¿Confirmar servicio realizado?
        </Typography>
        <Typography variant="body2" color="#737791" textAlign="center" sx={{ mb: 2 }}>
          <strong>{booking.servicioNombre}</strong> — {formatDate(booking.fecha)}
        </Typography>

        {/* PIN que el cliente comparte al terminar (su aceptación del servicio y extras).
            Reservas confirmadas antes de esta función no tienen PIN. */}
        {booking.tienePin && (
          <>
            <Typography variant="caption" color="#737791" display="block" textAlign="center" sx={{ mb: 1.5 }}>
              Pídele al cliente el <strong>PIN de confirmación</strong> que aparece en su reserva.
            </Typography>
            <TextField
              fullWidth
              placeholder="PIN de 4 dígitos"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', fontWeight: 900, fontSize: 22, letterSpacing: 8 } }}
              sx={{ mb: 2 }}
            />
          </>
        )}

        {totalExtras > 0 && (
          <Box sx={{
            bgcolor: extrasSinPagar ? '#FFFBEB' : '#F8FAFC',
            border: extrasSinPagar ? '1px solid #FFD074' : '1px solid #F1F5F9',
            borderRadius: '10px', p: 1.5, mb: 2
          }}>
            <Typography variant="caption" color={extrasSinPagar ? '#92400E' : booking.pago ? '#1B7A5A' : '#4B5563'}>
              {extrasSinPagar
                ? <>El cliente aún no paga <strong>${extrasRestante} MXN de extras</strong>. Pídele pagarlos desde su reserva; hasta entonces no podrás completar el servicio.</>
                : booking.pago
                ? <>✓ Los <strong>${totalExtras} MXN de extras</strong> ya están pagados.</>
                : <>Acuerda el cobro de los <strong>${totalExtras} MXN de extras</strong> directamente con el cliente.</>}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: '#E2E8F0', color: '#737791' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" disabled={loading || (booking.tienePin && pin.length < 4)} onClick={() => onConfirm(pin)}
            sx={{ bgcolor: '#59C39A', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&:hover': { bgcolor: '#4aad87' }, '&.Mui-disabled': { bgcolor: '#9DE1D5' } }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Confirmar'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Modal del proveedor para agregar un cargo extra durante el servicio
const ExtraModal = ({ open, onClose, onConfirm, loading }) => {
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState('');
  const [errores, setErrores] = useState({});
  useEffect(() => { if (open) { setConcepto(''); setMonto(''); setErrores({}); } }, [open]);

  const guardar = () => {
    const nuevos = {};
    if (!concepto.trim()) nuevos.concepto = 'Describe el extra';
    if (!(parseFloat(monto) > 0)) nuevos.monto = 'Monto inválido';
    if (Object.keys(nuevos).length) { setErrores(nuevos); return; }
    onConfirm(concepto.trim(), parseFloat(monto));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
      <DialogContent>
        <Typography variant="h6" fontWeight="900" color="#0E2465" sx={{ mb: 1 }}>
          Agregar extra
        </Typography>
        <Typography variant="caption" color="#737791" display="block" sx={{ mb: 2 }}>
          El cliente lo verá de inmediato en su reserva y se cobrará al completar el
          servicio, cuando te comparta su PIN (esa es su aceptación).
        </Typography>
        <TextField fullWidth label="Concepto" placeholder="Ej. Material adicional" value={concepto}
          onChange={(e) => { setConcepto(e.target.value); setErrores(p => ({ ...p, concepto: '' })); }}
          error={!!errores.concepto} helperText={errores.concepto || ''} size="small" sx={{ mb: 2 }} />
        <TextField fullWidth label="Monto (MXN)" placeholder="0.00" value={monto}
          onChange={(e) => { setMonto(e.target.value.replace(/[^\d.]/g, '')); setErrores(p => ({ ...p, monto: '' })); }}
          error={!!errores.monto} helperText={errores.monto || ''} size="small"
          inputProps={{ inputMode: 'decimal' }} sx={{ mb: parseFloat(monto) > 0 ? 0.75 : 2.5 }} />
        {/* El extra también paga el 10% de comisión de la plataforma */}
        {parseFloat(monto) > 0 && (() => {
          const c = parseFloat(monto);
          const comision = Math.round(c * 10) / 100;
          const neto = Math.round((c - comision) * 100) / 100;
          return (
            <Typography variant="caption" sx={{ display: 'block', mb: 2.5, color: '#1B7A5A' }}>
              Recibirás <strong>${neto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong> —
              comisión de la plataforma 10% (${comision.toLocaleString('es-MX', { minimumFractionDigits: 2 })}).
            </Typography>
          );
        })()}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.2, fontWeight: 700, borderColor: '#E2E8F0', color: '#737791' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" disabled={loading} onClick={guardar}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.2, fontWeight: 700 }}>
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Agregar'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const CancelarConfirmadaModal = ({ open, onClose, onConfirm, loading, booking, esProveedor, penalizacion }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
    slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
    <DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#FF827A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CancelOutlinedIcon sx={{ fontSize: 32, color: 'white' }} />
        </Box>
      </Box>

      <Typography variant="h6" fontWeight="900" color="#0E2465" textAlign="center" sx={{ mb: 1 }}>
        ¿Cancelar servicio confirmado?
      </Typography>
      <Typography variant="body2" color="#737791" textAlign="center" sx={{ mb: 2 }}>
        <strong>{booking?.servicioNombre}</strong> — {formatDate(booking?.fecha)}
      </Typography>

      {/* Resultado según la política de cancelación */}
      {esProveedor ? (
        <Box sx={{ bgcolor: '#FFF3CD', border: '1px solid #FFD074', borderRadius: '12px', p: 2, mb: 2.5 }}>
          <Typography variant="caption" color="#92400E" sx={{ lineHeight: 1.6 }}>
            El pago del cliente se le reembolsará por completo. Cancelar servicios
            confirmados afecta la confianza en tu perfil; hazlo solo si es necesario.
          </Typography>
        </Box>
      ) : penalizacion ? (
        <Box sx={{ bgcolor: '#FFF3CD', border: '1px solid #FFD074', borderRadius: '12px', p: 2, mb: 2.5 }}>
          <Typography variant="body2" fontWeight="900" color="#92400E" sx={{ mb: 0.5 }}>
            ⚠️ Cancelación con penalización del {penalizacion.pct}%
          </Typography>
          <Typography variant="caption" color="#92400E" sx={{ lineHeight: 1.6 }}>
            Por cancelar con menos de 24 horas de anticipación, de tu pago de{' '}
            <strong>${penalizacion.monto} MXN</strong> se te reembolsarán{' '}
            <strong>${penalizacion.devuelto} MXN</strong>; el resto se entrega al
            proveedor como compensación por el tiempo que apartó.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ bgcolor: '#D6F3E8', border: '1px solid #9DE1D5', borderRadius: '12px', p: 2, mb: 2.5 }}>
          <Typography variant="caption" color="#1B7A5A" sx={{ lineHeight: 1.6 }}>
            Cancelación sin costo. {booking?.pago?.status === 'retenido'
              ? 'Se te reembolsará el total de tu pago.'
              : 'No se ha realizado ningún cobro.'}
          </Typography>
        </Box>
      )}

      <Typography variant="caption" color="#737791" display="block" textAlign="center" sx={{ mb: 2.5 }}>
        {esProveedor
          ? 'El cliente será notificado de la cancelación.'
          : 'El proveedor será notificado de la cancelación.'}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button fullWidth variant="outlined" onClick={onClose}
          sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: '#E2E8F0', color: '#737791' }}>
          Volver
        </Button>
        <Button fullWidth variant="contained" disabled={loading} onClick={onConfirm}
          sx={{ bgcolor: '#FF827A', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
            '&:hover': { bgcolor: '#e06f68' }, '&.Mui-disabled': { bgcolor: '#fca5a5' } }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Sí, cancelar'}
        </Button>
      </Box>
    </DialogContent>
  </Dialog>
);

const CalificarModal = ({ open, onClose, onConfirm, loading, esProveedor }) => {
  const [rating, setRating] = useState(0);
  const [comentario, setComentario] = useState('');

  const handleClose = () => {
    setRating(0);
    setComentario('');
    onClose();
  };

  const handleSubmit = () => {
    if (!rating) return;
    onConfirm(rating, comentario);
    setRating(0);
    setComentario('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
      <DialogContent>
        <Typography variant="h6" fontWeight="900" color="#0E2465" textAlign="center" sx={{ mb: 0.5 }}>
          {esProveedor ? 'Calificar al cliente' : 'Calificar el servicio'}
        </Typography>
        <Typography variant="body2" color="#737791" textAlign="center" sx={{ mb: 2.5 }}>
          {esProveedor
            ? 'Tu opinión sobre el cliente ayuda a la comunidad.'
            : 'Tu opinión ayuda a otros usuarios a elegir mejor.'}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Rating
            value={rating}
            onChange={(_, v) => setRating(v)}
            size="large"
            sx={{ fontSize: '2.5rem', color: '#0E2465' }}
          />
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder={esProveedor ? 'Cuéntanos cómo fue la experiencia con el cliente (opcional)' : 'Cuéntanos cómo fue tu experiencia (opcional)'}
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          inputProps={{ maxLength: 500 }}
          sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={handleClose}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: '#E2E8F0', color: '#737791' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" disabled={loading || !rating} onClick={handleSubmit}
            sx={{ bgcolor: '#7495f8', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&:hover': { bgcolor: '#5a7de0' }, '&.Mui-disabled': { bgcolor: '#c7d3fc' } }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Publicar'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const BookingCard = ({ booking, tieneTicket, onAccept, onDecline, onComplete, onModificar, onResponderCambio, onAddExtra, onRemoveExtra, onReportar, onAclarar, loading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [declineOpen, setDeclineOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [completarOpen, setCompletarOpen] = useState(false);
  const [modificarOpen, setModificarOpen] = useState(false);
  const [disputaOpen, setDisputaOpen] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [aclaracionOpen, setAclaracionOpen] = useState(false);
  const [cancelConfirmadaOpen, setCancelarConfirmadaOpen] = useState(false);
  const [extraOpen, setExtraOpen] = useState(false);
  const { servicioNombre, fecha, hora, duracionMinutos, modalidad, nota, status, cliente } = booking;
  const clientName = cliente ? `${cliente.name} ${cliente.apellido}` : 'Cliente';
  const avatarSrc = cliente?.avatar
    ? `${urlImg}/users/${cliente._id}/${cliente.avatar}?v=${cliente.versionAvatar || 0}`
    : null;

  const st = STATUS_COLOR[status] || STATUS_COLOR.pendiente;
  const isPending = status === 'pendiente';
  const today = new Date().toISOString().split('T')[0];
  const canComplete = status === 'confirmada' && fecha <= today;
  const canCancelConfirmada = status === 'confirmada' && fecha > today;
  const canModify = ['pendiente', 'confirmada'].includes(status) && !booking.modificacion?.solicitada && fecha >= today;
  // Con solicitadaPor vacío (solicitudes viejas) el cambio lo pidió el cliente
  const cambioDelProveedor = booking.modificacion?.solicitadaPor === 'proveedor';

  // Estado del pago de extras: el cliente los paga desde su reserva y hasta entonces
  // se puede completar el servicio
  const totalExtras = Math.round((booking.extras || []).reduce((a, e) => a + e.monto, 0) * 100) / 100;
  const extrasEnProceso = !!booking.pagoExtra?.enProceso;
  const extrasRestante = Math.round((totalExtras - (booking.pagoExtra?.pagado || 0)) * 100) / 100;

  // Disputa: cuando el servicio ya debió ocurrir y aún no se completa, o durante la
  // semana de retención (completado con el pago aún en custodia, sin dispersar).
  const servicioPasado = new Date(`${fecha}T${hora}:00`).getTime() <= Date.now();
  const disputaAbierta = !!booking.disputa?.abierta;
  const enRetencion = status === 'completada' && booking.pago?.status === 'retenido' && !disputaAbierta;
  // Solo se puede reportar/aclarar dentro de los últimos 3 meses (por fecha del servicio)
  const canReport = ((status === 'confirmada' && servicioPasado) || enRetencion) && !disputaAbierta && dentroVentanaReporte(fecha);
  // Historial: reserva finalizada → botón "Ver detalles" (modal) y, si hay factura, PDF/XML
  const esFinalizado = status === 'completada';
  const facturaEmitida = booking.factura?.estado === 'emitida';

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: '1px solid #F1F5F9', p: 3, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight="900" color="#0E2465">
            {servicioNombre || 'Servicio'}
          </Typography>
          <Chip label={st.label} size="small"
            sx={{ bgcolor: st.bgcolor, color: st.color, fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px' }} />
          {/* Estado del pago: clave para no dar el servicio sin cobro confirmado (SPEI/tienda) */}
          {booking.pago && PAGO_STATUS[booking.pago.status] && (
            <Chip
              label={`${PAGO_STATUS[booking.pago.status].label} · $${booking.pago.monto}`}
              size="small"
              sx={{
                bgcolor: PAGO_STATUS[booking.pago.status].bgcolor,
                color: PAGO_STATUS[booking.pago.status].color,
                fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px'
              }} />
          )}
        </Box>
      </Box>

      {/* Nota */}
      {nota && (
        <Typography variant="body2" color="#4B5563" sx={{
          mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}>
          <strong>Mensaje del usuario: </strong>{nota}
        </Typography>
      )}

      {/* Info row */}
      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 16, color: '#737791' }} />
          <Typography variant="caption" color="#4B5563">
            {minutesToLabel(hora)} - {minutesToLabel(hora, duracionMinutos)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {modalidad === 'Remoto'
            ? <LaptopIcon sx={{ fontSize: 16, color: '#737791' }} />
            : modalidad === 'Domicilio'
            ? <HomeIcon sx={{ fontSize: 16, color: '#737791' }} />
            : <LocationOnIcon sx={{ fontSize: 16, color: '#737791' }} />}
          <Typography variant="caption" color="#4B5563">{modalidad}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 16, color: '#737791' }} />
          <Typography variant="caption" color="#4B5563">{formatDate(fecha)}</Typography>
        </Box>
      </Box>

      {/* Disputa abierta: el pago está congelado hasta que la plataforma resuelva */}
      {disputaAbierta && (
        <Box sx={{ bgcolor: '#FFE0DD', border: '1px solid #FF827A', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="#B23B33" sx={{ display: 'block', mb: 0.5 }}>
            🚩 Disputa abierta {booking.disputa.abiertaPor === 'proveedor' ? '(la abriste tú)' : '(la abrió el cliente)'}
          </Typography>
          <Typography variant="caption" color="#B23B33">
            El pago está congelado mientras el equipo de la plataforma revisa el caso.
            Te avisaremos la resolución.
          </Typography>
        </Box>
      )}

      {/* Banner solicitud de cambio: del cliente (responder) o propia (en espera) */}
      {booking.modificacion?.solicitada && !cambioDelProveedor && (
        <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FFD074', borderRadius: '10px', p: 2, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="#92400E" sx={{ display: 'block', mb: 1 }}>
            ⚡ El cliente solicita cambiar la fecha
          </Typography>
          <Stack direction="row" spacing={3} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="#737791">Fecha actual</Typography>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ display: 'block' }}>
                {formatDate(booking.fecha)} — {minutesToLabel(booking.hora)}
              </Typography>
            </Box>
            <Typography variant="caption" color="#FFD074" sx={{ alignSelf: 'center', fontSize: '1.1rem' }}>→</Typography>
            <Box>
              <Typography variant="caption" color="#737791">Fecha solicitada</Typography>
              <Typography variant="caption" fontWeight="bold" color="#92400E" sx={{ display: 'block' }}>
                {formatDate(booking.modificacion.fechaNueva)} — {minutesToLabel(booking.modificacion.horaNueva)}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" disabled={loading}
              onClick={() => onResponderCambio(booking._id, false)}
              sx={{ bgcolor: '#FF827A', textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                '&:hover': { bgcolor: '#e06f68' }, '&.Mui-disabled': { bgcolor: '#fca5a5' } }}>
              Rechazar cambio
            </Button>
            <Button size="small" variant="contained" disabled={loading}
              onClick={() => onResponderCambio(booking._id, true)}
              sx={{ bgcolor: '#59C39A', textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                '&:hover': { bgcolor: '#4aad87' }, '&.Mui-disabled': { bgcolor: '#9DE1D5' } }}>
              Aceptar cambio
            </Button>
          </Box>
        </Box>
      )}

      {booking.modificacion?.solicitada && cambioDelProveedor && (
        <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FFD074', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="#92400E" sx={{ display: 'block', mb: 0.5 }}>
            ⏳ Propusiste una nueva fecha, en espera del cliente
          </Typography>
          <Typography variant="caption" color="#737791">
            Propusiste: <strong style={{ color: '#92400E' }}>
              {formatDate(booking.modificacion.fechaNueva)} — {minutesToLabel(booking.modificacion.horaNueva)}
            </strong>
          </Typography>
        </Box>
      )}

      {/* Extras agregados durante el servicio (editable mientras esté confirmado) */}
      {booking.extras?.length > 0 && (
        <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ display: 'block', mb: 0.5 }}>
            Extras del servicio
          </Typography>
          {booking.extras.map((e) => (
            <Box key={e._id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="#4B5563">{e.concepto}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" fontWeight={700} color="#0E2465">${e.monto}</Typography>
                {status === 'confirmada' && (
                  <IconButton size="small" disabled={loading} onClick={() => onRemoveExtra(booking._id, e._id)}
                    sx={{ p: 0.25, color: '#FF827A' }}>
                    <CancelOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}
          <Divider sx={{ my: 0.5, borderColor: '#F1F5F9' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" fontWeight={700} color="#0E2465">Total extras</Typography>
            <Typography variant="caption" fontWeight={900} color="#0E2465">${totalExtras}</Typography>
          </Box>

          {/* El cliente paga los extras desde su reserva; hasta entonces no se puede completar */}
          {booking.pago && status === 'confirmada' && (
            extrasEnProceso ? (
              <Typography variant="caption" color="#A8740F" sx={{ display: 'block', mt: 0.5 }}>
                ⏳ El cliente generó su referencia de pago; en cuanto se confirme podrás finalizar.
              </Typography>
            ) : extrasRestante <= 0 ? (
              <Typography variant="caption" color="#1B7A5A" sx={{ display: 'block', mt: 0.5 }}>
                ✓ Extras pagados por el cliente. Ya puedes finalizar el servicio con su PIN.
              </Typography>
            ) : (
              <Typography variant="caption" color="#A8740F" sx={{ display: 'block', mt: 0.5 }}>
                El cliente debe pagar los extras desde su reserva antes de que puedas finalizar el servicio.
              </Typography>
            )
          )}
        </Box>
      )}

      <Divider sx={{ mb: 1.5, borderColor: '#F1F5F9' }} />

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box
          onClick={() => cliente?._id && navigate(`/user/cliente/${cliente._id}`)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            cursor: cliente?._id ? 'pointer' : 'default',
            '&:hover .client-name': cliente?._id ? { textDecoration: 'underline' } : {}
          }}
        >
          <Typography variant="caption" color="#737791">Info del cliente</Typography>
          <Avatar src={avatarSrc} sx={{ width: 28, height: 28, fontSize: '0.7rem' }}>
            {!avatarSrc && clientName.charAt(0)}
          </Avatar>
          <Typography className="client-name" variant="body2" fontWeight="bold" color="#0E2465">{clientName}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {tieneTicket && (
            <Button size="small" variant="outlined"
              startIcon={<SupportAgentOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(`/user/tickets?booking=${booking._id}`)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', borderColor: '#7495F8', color: '#0E2465',
                '&:hover': { borderColor: '#5f83e8', bgcolor: '#EEF2FF' } }}>
              Ver ticket
            </Button>
          )}
          {esFinalizado ? (
            <>
              {facturaEmitida && <FacturaBotones factura={booking.factura} />}
              <Button size="small" variant="contained" disableElevation
                startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setDetalleOpen(true)}
                sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#0E2465', fontSize: '12px',
                  '&:hover': { bgcolor: '#0a1a4a' } }}>
                Ver detalles
              </Button>
            </>
          ) : (
            <Button size="small" variant="outlined"
              startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
              disabled={!cliente?._id}
              onClick={() => dispatch(startChat(cliente._id, booking.servicio, 'service'))}
              sx={{ textTransform: 'none', borderRadius: '8px', borderColor: '#64D2B1', color: '#059669', fontSize: '12px',
                '&:hover': { bgcolor: '#ECFDF5', borderColor: '#059669' } }}>
              Contactar usuario
            </Button>
          )}
          {canModify && (
            <Button size="small" variant="contained" disabled={loading}
              startIcon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
              onClick={() => setModificarOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#FFD074', color: '#92400E', fontSize: '12px',
                '&:hover': { bgcolor: '#f0c35a' }, '&.Mui-disabled': { bgcolor: '#FFF3CD' } }}>
              Modificar fecha
            </Button>
          )}
          {isPending && (
            <>
              <Button size="small" variant="contained" disabled={loading}
                startIcon={<CancelOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setDeclineOpen(true)}
                sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#FF827A', fontSize: '12px',
                  '&:hover': { bgcolor: '#e06f68' }, '&.Mui-disabled': { bgcolor: '#fca5a5' } }}>
                Declinar
              </Button>
              <Button size="small" variant="contained" disabled={loading}
                startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                onClick={() => setAcceptOpen(true)}
                sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#0E2465', fontSize: '12px',
                  '&:hover': { bgcolor: '#0a1a4a' }, '&.Mui-disabled': { bgcolor: '#94A3B8' } }}>
                Aceptar
              </Button>
            </>
          )}
          {status === 'confirmada' && (
            <Button size="small" variant="outlined" disabled={loading}
              onClick={() => setExtraOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', borderColor: '#FFD074', color: '#92400E', fontSize: '12px',
                '&:hover': { bgcolor: '#FFFBEB', borderColor: '#f0c35a' } }}>
              + Extra
            </Button>
          )}
          {canReport && (
            <Button size="small" variant="outlined" disabled={loading}
              startIcon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              onClick={() => setDisputaOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', borderColor: '#FF827A', color: '#B23B33', fontSize: '12px',
                '&:hover': { bgcolor: '#FFE0DD', borderColor: '#e06f68' } }}>
              Reportar problema
            </Button>
          )}
          {canComplete && !disputaAbierta && (
            <Button size="small" variant="contained" disabled={loading}
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
              onClick={() => setCompletarOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#59C39A', fontSize: '12px',
                '&:hover': { bgcolor: '#4aad87' }, '&.Mui-disabled': { bgcolor: '#9DE1D5' } }}>
              Servicio realizado
            </Button>
          )}
          {canCancelConfirmada && (
            <Button size="small" variant="contained" disabled={loading} disableElevation
              startIcon={<CancelOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => setCancelarConfirmadaOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#FF827A', color: 'white', fontSize: '12px',
                '&:hover': { bgcolor: '#e06f68' } }}>
              Cancelar servicio
            </Button>
          )}
        </Box>
      </Box>

      <ModificarModal
        open={modificarOpen}
        onClose={() => setModificarOpen(false)}
        loading={loading}
        booking={booking}
        esProveedor
        onConfirm={(id, fechaNueva, horaNueva) => {
          setModificarOpen(false);
          onModificar(id, fechaNueva, horaNueva);
        }}
      />

      <ReporteServicioModal
        open={disputaOpen}
        onClose={() => setDisputaOpen(false)}
        loading={loading}
        esProveedor
        booking={booking}
        onConfirm={async (payload) => {
          const ok = await onReportar(booking._id, payload);
          if (ok) setDisputaOpen(false);
        }}
      />

      <DetalleReservaModal
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        booking={booking}
        personaNombre={clientName}
        enRetencion={enRetencion}
        puedeReportar={dentroVentanaReporte(fecha)}
        onReportar={() => { setDetalleOpen(false); (enRetencion ? setDisputaOpen(true) : setAclaracionOpen(true)); }}
      />

      <ReporteServicioModal
        open={aclaracionOpen}
        onClose={() => setAclaracionOpen(false)}
        loading={loading}
        esProveedor
        modo="aclaracion"
        booking={booking}
        onConfirm={async (payload) => {
          const ok = await onAclarar(booking._id, payload);
          if (ok) setAclaracionOpen(false);
        }}
      />

      <DeclineModal
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        loading={loading}
        onConfirm={(motivo) => {
          setDeclineOpen(false);
          onDecline(booking._id, motivo);
        }}
      />

      <AcceptModal
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        loading={loading}
        booking={booking}
        onConfirm={() => {
          setAcceptOpen(false);
          onAccept(booking._id);
        }}
      />

      <CompletarModal
        open={completarOpen}
        onClose={() => setCompletarOpen(false)}
        loading={loading}
        booking={booking}
        onConfirm={(pin) => {
          setCompletarOpen(false);
          onComplete(booking._id, pin);
        }}
      />

      <ExtraModal
        open={extraOpen}
        onClose={() => setExtraOpen(false)}
        loading={loading}
        onConfirm={(concepto, monto) => {
          setExtraOpen(false);
          onAddExtra(booking._id, concepto, monto);
        }}
      />

      <CancelarConfirmadaModal
        open={cancelConfirmadaOpen}
        onClose={() => setCancelarConfirmadaOpen(false)}
        loading={loading}
        booking={booking}
        esProveedor={true}
        onConfirm={() => {
          setCancelarConfirmadaOpen(false);
          onDecline(booking._id, 'Cancelado por el proveedor');
        }}
      />
    </Box>
  );
};

// Estado del pago de una reserva: referencia SPEI/tienda pendiente, o confirmación
const PAGO_STATUS = {
  pendiente: { label: 'Pago pendiente', bgcolor: '#FFF1D6', color: '#A8740F' },
  retenido: { label: 'Pagado', bgcolor: '#D6F3E8', color: '#1B7A5A' },
  liberado: { label: 'Pagado', bgcolor: '#D6F3E8', color: '#1B7A5A' },
  reembolsado: { label: 'Reembolsado', bgcolor: '#E0E7FF', color: '#3730A3' },
  devolucion_pendiente: { label: 'Devolución en proceso', bgcolor: '#FFF1D6', color: '#A8740F' },
};

// Fecha de vencimiento legible de una referencia
const fmtVence = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return ''; }
};

// Datos de una referencia OXXO/SPEI para que el cliente la pague
const ReferenciaBloque = ({ ref, metodo, monto }) => {
  if (!ref) return null;
  const Dato = ({ label, value, mono }) => (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 10.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: mono ? 15 : 13.5, fontFamily: mono ? 'monospace' : 'inherit', fontWeight: 700, color: '#0E2465', wordBreak: 'break-all' }}>{value || '—'}</Typography>
    </Box>
  );
  return (
    <Box sx={{ mt: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', p: 2 }}>
      {metodo === 'spei' ? (
        <>
          <Dato label="Banco" value={ref.banco} />
          <Dato label="CLABE" value={ref.clabe} mono />
          {ref.referencia && <Dato label="Referencia / Concepto" value={ref.referencia} mono />}
          <Dato label="Monto exacto" value={`$${monto} MXN`} />
        </>
      ) : (
        <>
          <Dato label="Referencia OXXO" value={ref.referencia} mono />
          <Dato label="Monto" value={`$${monto} MXN`} />
        </>
      )}
      {ref.dueDate && (
        <Typography sx={{ fontSize: 11.5, color: '#A8740F', fontWeight: 600, mt: 0.5 }}>
          Vence: {fmtVence(ref.dueDate)}
        </Typography>
      )}
      {ref.barcodeUrl && (
        <Button
          size="small" variant="outlined" href={ref.barcodeUrl} target="_blank" rel="noopener"
          sx={{ mt: 1, textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
        >
          {metodo === 'oxxo' ? 'Ver / descargar ficha' : 'Ver instrucciones de pago'}
        </Button>
      )}
    </Box>
  );
};

const PagoDialog = ({ open, onClose, bookingId }) => {
  const [pago, setPago] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [verificando, setVerificando] = useState(false);
  const [verifMsg, setVerifMsg] = useState('');

  useEffect(() => {
    if (!open || !bookingId) return;
    setCargando(true);
    setVerifMsg('');
    getPagoBooking(bookingId).then((data) => {
      setPago(data);
      setCargando(false);
    });
  }, [open, bookingId]);

  const handleVerificar = async () => {
    setVerificando(true);
    setVerifMsg('');
    const data = await verificarPagoBooking(bookingId);
    setVerificando(false);
    if (data) {
      setPago(data);
      setVerifMsg(data.confirmado ? '✓ ¡Pago confirmado!' : 'Aún no vemos tu pago reflejado. Si acabas de pagar, puede tardar unos minutos.');
    } else {
      setVerifMsg('No pudimos verificar el pago en este momento. Intenta de nuevo.');
    }
  };

  const st = pago ? (PAGO_STATUS[pago.status] || null) : null;
  const refPendiente = pago && pago.status === 'pendiente' && pago.metodo !== 'card';
  const extraRefPendiente = pago?.extra && pago.extra.status === 'pendiente' && pago.extra.metodo !== 'card';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="900" color="#0E2465" sx={{ mb: 2 }}>
          Datos de pago
        </Typography>

        {cargando ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={26} /></Box>
        ) : !pago ? (
          <Typography variant="body2" color="#737791">Esta reserva no requirió pago en línea.</Typography>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" fontWeight="900" color="#0E2465">
                ${pago.monto} <small style={{ fontSize: 13, color: '#737791' }}>{pago.moneda}</small>
              </Typography>
              {st && (
                <Chip label={st.label} size="small"
                  sx={{ bgcolor: st.bgcolor, color: st.color, fontWeight: 700, borderRadius: '6px' }} />
              )}
            </Box>

            {pago.status === 'pendiente' && pago.metodo === 'card' && (
              <Typography variant="body2" color="#737791">
                Tu tarjeta {pago.tarjeta?.last4 ? `terminación ${pago.tarjeta.last4} ` : ''}se cobrará cuando el
                proveedor acepte tu reserva.
              </Typography>
            )}

            {/* Referencia OXXO/SPEI del pago principal por pagar */}
            {refPendiente && (
              <Box>
                <Typography variant="body2" color="#737791" sx={{ mb: 0.5 }}>
                  {pago.metodo === 'spei'
                    ? 'Haz la transferencia SPEI con estos datos. Tu lugar se aparta al confirmarse el pago.'
                    : 'Paga en efectivo en cualquier OXXO con esta ficha. Tu lugar se aparta al confirmarse el pago.'}
                </Typography>
                <ReferenciaBloque ref={pago.referenciaPago} metodo={pago.metodo} monto={pago.monto} />
                <Button
                  fullWidth variant="contained" disableElevation onClick={handleVerificar} disabled={verificando}
                  sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, borderRadius: '10px', bgcolor: '#0E2465', '&:hover': { bgcolor: '#0a1a4a' } }}
                >
                  {verificando ? 'Verificando…' : 'Ya pagué, verificar'}
                </Button>
              </Box>
            )}

            {['retenido', 'liberado'].includes(pago.status) && (
              <Typography variant="body2" color="#737791">
                Tu pago está confirmado{pago.metodo === 'card' && pago.tarjeta?.last4 ? ` (tarjeta terminación ${pago.tarjeta.last4})` : ''}.
                El monto queda protegido hasta que el servicio se complete.
              </Typography>
            )}

            {pago.status === 'devolucion_pendiente' && (
              <Typography variant="body2" color="#737791">
                Estamos procesando la devolución de tu pago. Te contactaremos para completarla.
              </Typography>
            )}

            {pago.status === 'reembolsado' && (
              <Typography variant="body2" color="#737791">
                Tu pago fue reembolsado a tu tarjeta. El banco puede tardar unos días en reflejarlo.
              </Typography>
            )}

            {/* Cobro de extras del servicio (si existe) */}
            {pago.extra && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F1F5F9' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={700} color="#0E2465">
                    Extras del servicio — ${pago.extra.monto} {pago.extra.moneda}
                  </Typography>
                  {PAGO_STATUS[pago.extra.status] && (
                    <Chip label={PAGO_STATUS[pago.extra.status].label} size="small"
                      sx={{ bgcolor: PAGO_STATUS[pago.extra.status].bgcolor, color: PAGO_STATUS[pago.extra.status].color, fontWeight: 700, fontSize: 11, borderRadius: '6px' }} />
                  )}
                </Box>

                {pago.extra.status === 'fallido' && (
                  <Typography variant="caption" color="#B23B33">
                    No pudimos cobrar los extras a tu tarjeta. Contacta al proveedor para acordar el pago.
                  </Typography>
                )}

                {/* Referencia OXXO/SPEI de los extras por pagar */}
                {extraRefPendiente && (
                  <>
                    <ReferenciaBloque ref={pago.extra.referenciaPago} metodo={pago.extra.metodo} monto={pago.extra.monto} />
                    {!refPendiente && (
                      <Button
                        fullWidth variant="contained" disableElevation onClick={handleVerificar} disabled={verificando}
                        sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, borderRadius: '10px', bgcolor: '#0E2465', '&:hover': { bgcolor: '#0a1a4a' } }}
                      >
                        {verificando ? 'Verificando…' : 'Ya pagué, verificar'}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}

            {verifMsg && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1.5, fontWeight: 700, color: verifMsg.startsWith('✓') ? '#1B7A5A' : '#A8740F' }}>
                {verifMsg}
              </Typography>
            )}
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: '#737791' }}>Cerrar</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const ClientBookingCard = ({ booking, tieneTicket, onCancel, onModificar, onResponderCambio, onPagarExtras, onReportar, onAclarar, loading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [modificarOpen, setModificarOpen] = useState(false);
  const [cancelConfirmadaOpen, setCancelarConfirmadaOpen] = useState(false);
  const [pagoOpen, setPagoOpen] = useState(false);
  const [pagarExtrasOpen, setPagarExtrasOpen] = useState(false);
  const [disputaOpen, setDisputaOpen] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [aclaracionOpen, setAclaracionOpen] = useState(false);
  const { servicioNombre, fecha, hora, duracionMinutos, modalidad, nota, status, proveedor } = booking;
  const providerName = proveedor ? `${proveedor.name} ${proveedor.apellido}` : 'Proveedor';
  const avatarSrc = proveedor?.avatar
    ? `${urlImg}/users/${proveedor._id}/${proveedor.avatar}?v=${proveedor.versionAvatar || 0}`
    : null;

  const st = STATUS_COLOR[status] || STATUS_COLOR.pendiente;
  const isPending = status === 'pendiente';
  const today = new Date().toISOString().split('T')[0];
  // En pendiente el cambio es directo (el proveedor aún no acepta); en confirmada es solicitud
  const canModify = ['pendiente', 'confirmada'].includes(status) && !booking.modificacion?.solicitada && fecha >= today;
  const cambioDelProveedor = booking.modificacion?.solicitadaPor === 'proveedor';

  // Política de cancelación (confirmadas): gratis la 1.ª hora tras reservar o si faltan
  // +24 h; después se retiene 30% para el proveedor; con menos de 3 h ya no se cancela
  const horasParaServicio = (new Date(`${fecha}T${hora}:00`).getTime() - Date.now()) / 36e5;
  const enGracia = booking.createdAt && Date.now() - new Date(booking.createdAt).getTime() <= 36e5;
  const cancelGratis = enGracia || horasParaServicio > 24;
  const canCancelConfirmada = status === 'confirmada' && horasParaServicio > 3;
  // Solo hay retención real si el pago principal ya está en custodia
  const penalizacion = !cancelGratis && booking.pago?.status === 'retenido'
    ? { pct: 30, monto: booking.pago.monto, devuelto: Math.round(booking.pago.monto * 70) / 100 }
    : null;

  // Disputa: cuando el servicio ya debió ocurrir y aún no se completa, o durante la
  // semana de retención (completado con el pago aún en custodia, sin dispersar).
  const disputaAbierta = !!booking.disputa?.abierta;
  const enRetencion = status === 'completada' && booking.pago?.status === 'retenido' && !disputaAbierta;
  // Solo se puede reportar/aclarar dentro de los últimos 3 meses (por fecha del servicio)
  const canReport = ((status === 'confirmada' && horasParaServicio <= 0) || enRetencion) && !disputaAbierta && dentroVentanaReporte(fecha);
  const esFinalizado = status === 'completada';
  const facturaEmitida = booking.factura?.estado === 'emitida';

  // Pago de extras: se pagan ANTES de que el proveedor pueda completar el servicio.
  // pagoExtra viene agregado del backend: pagado (suma confirmada) + enProceso (referencia sin pagar)
  const totalExtras = Math.round((booking.extras || []).reduce((a, e) => a + e.monto, 0) * 100) / 100;
  const extrasPagado = booking.pagoExtra?.pagado || 0;
  const extrasEnProceso = !!booking.pagoExtra?.enProceso;
  const extrasRestante = Math.round((totalExtras - extrasPagado) * 100) / 100;
  const principalPagado = booking.pago && ['retenido', 'liberado'].includes(booking.pago.status);
  const puedePagarExtras = ['confirmada', 'completada'].includes(status)
    && principalPagado && extrasRestante > 0 && !extrasEnProceso;

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: '1px solid #F1F5F9', p: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight="900" color="#0E2465">
            {servicioNombre || 'Servicio'}
          </Typography>
          <Chip label={st.label} size="small"
            sx={{ bgcolor: st.bgcolor, color: st.color, fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px' }} />
          {booking.pago && PAGO_STATUS[booking.pago.status] && (
            <Chip
              label={PAGO_STATUS[booking.pago.status].label}
              size="small"
              sx={{
                bgcolor: PAGO_STATUS[booking.pago.status].bgcolor,
                color: PAGO_STATUS[booking.pago.status].color,
                fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px'
              }} />
          )}
        </Box>
      </Box>

      {nota && (
        <Typography variant="body2" color="#4B5563" sx={{
          mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}>
          <strong>Tu mensaje: </strong>{nota}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 16, color: '#737791' }} />
          <Typography variant="caption" color="#4B5563">
            {minutesToLabel(hora)} - {minutesToLabel(hora, duracionMinutos)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {modalidad === 'Remoto'
            ? <LaptopIcon sx={{ fontSize: 16, color: '#737791' }} />
            : modalidad === 'Domicilio'
            ? <HomeIcon sx={{ fontSize: 16, color: '#737791' }} />
            : <LocationOnIcon sx={{ fontSize: 16, color: '#737791' }} />}
          <Typography variant="caption" color="#4B5563">{modalidad}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 16, color: '#737791' }} />
          <Typography variant="caption" color="#4B5563">{formatDate(fecha)}</Typography>
        </Box>
      </Box>

      {/* Disputa abierta: el pago está congelado hasta que la plataforma resuelva */}
      {disputaAbierta && (
        <Box sx={{ bgcolor: '#FFE0DD', border: '1px solid #FF827A', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="#B23B33" sx={{ display: 'block', mb: 0.5 }}>
            🚩 Disputa abierta {booking.disputa.abiertaPor === 'cliente' ? '(la abriste tú)' : '(la abrió el proveedor)'}
          </Typography>
          <Typography variant="caption" color="#B23B33">
            Tu pago está congelado mientras el equipo de la plataforma revisa el caso.
            Te avisaremos la resolución.
          </Typography>
        </Box>
      )}

      {/* Aviso de auto-completado: hay 3 días para reportar problemas */}
      {canReport && (
        <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" color="#737791">
            ¿El servicio ya ocurrió? Si todo salió bien, comparte tu PIN al proveedor.
            Si algo salió mal, tienes <strong>3 días</strong> para reportar un problema;
            después el servicio se completa automáticamente y el pago se libera.
          </Typography>
        </Box>
      )}

      {/* Badge de modificación pendiente: propia (en espera) o del proveedor (responder) */}
      {booking.modificacion?.solicitada && !cambioDelProveedor && (
        <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FFD074', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="#92400E" sx={{ display: 'block', mb: 0.5 }}>
            ⏳ Cambio de fecha en espera de aprobación
          </Typography>
          <Typography variant="caption" color="#737791">
            Solicitaste: <strong style={{ color: '#92400E' }}>
              {formatDate(booking.modificacion.fechaNueva)} — {minutesToLabel(booking.modificacion.horaNueva)}
            </strong>
          </Typography>
        </Box>
      )}

      {booking.modificacion?.solicitada && cambioDelProveedor && (
        <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FFD074', borderRadius: '10px', p: 2, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="#92400E" sx={{ display: 'block', mb: 1 }}>
            ⚡ El proveedor propone cambiar la fecha
          </Typography>
          <Stack direction="row" spacing={3} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="#737791">Fecha actual</Typography>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ display: 'block' }}>
                {formatDate(booking.fecha)} — {minutesToLabel(booking.hora)}
              </Typography>
            </Box>
            <Typography variant="caption" color="#FFD074" sx={{ alignSelf: 'center', fontSize: '1.1rem' }}>→</Typography>
            <Box>
              <Typography variant="caption" color="#737791">Fecha propuesta</Typography>
              <Typography variant="caption" fontWeight="bold" color="#92400E" sx={{ display: 'block' }}>
                {formatDate(booking.modificacion.fechaNueva)} — {minutesToLabel(booking.modificacion.horaNueva)}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" disabled={loading}
              onClick={() => onResponderCambio(booking._id, false)}
              sx={{ bgcolor: '#FF827A', textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                '&:hover': { bgcolor: '#e06f68' }, '&.Mui-disabled': { bgcolor: '#fca5a5' } }}>
              Rechazar cambio
            </Button>
            <Button size="small" variant="contained" disabled={loading}
              onClick={() => onResponderCambio(booking._id, true)}
              sx={{ bgcolor: '#59C39A', textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                '&:hover': { bgcolor: '#4aad87' }, '&.Mui-disabled': { bgcolor: '#9DE1D5' } }}>
              Aceptar cambio
            </Button>
          </Box>
          {isPending && (
            <Typography variant="caption" color="#737791" sx={{ display: 'block', mt: 1 }}>
              El proveedor propuso esta fecha, así que al aceptarla la reserva queda
              confirmada de inmediato y se procesa tu pago.
            </Typography>
          )}
        </Box>
      )}

      {/* PIN de confirmación: el cliente lo da al proveedor SOLO al terminar bien */}
      {status === 'confirmada' && booking.pinConfirmacion && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#EEF2FF', border: '1px solid #80A3FF', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Box sx={{ bgcolor: 'white', borderRadius: '8px', px: 2, py: 0.5, border: '1px dashed #7495F8' }}>
            <Typography sx={{ fontWeight: 900, fontSize: 22, letterSpacing: 4, color: '#0E2465' }}>
              {booking.pinConfirmacion}
            </Typography>
          </Box>
          <Typography variant="caption" color="#4B5563" sx={{ flex: 1 }}>
            <strong>PIN de confirmación.</strong> Dáselo al proveedor únicamente cuando el servicio
            haya terminado a tu satisfacción — al compartirlo aceptas el servicio y sus extras.
            {extrasRestante > 0 && (
              <> <strong style={{ color: '#92400E' }}>Hay extras pendientes de pago: págalos abajo antes de dar tu PIN.</strong></>
            )}
          </Typography>
        </Box>
      )}

      {/* Extras agregados por el proveedor durante el servicio */}
      {booking.extras?.length > 0 && (
        <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ display: 'block', mb: 0.5 }}>
            Extras del servicio
          </Typography>
          {booking.extras.map((e) => (
            <Box key={e._id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="#4B5563">{e.concepto}</Typography>
              <Typography variant="caption" fontWeight={700} color="#0E2465">${e.monto}</Typography>
            </Box>
          ))}
          <Divider sx={{ my: 0.5, borderColor: '#F1F5F9' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" fontWeight={700} color="#0E2465">Total extras</Typography>
            <Typography variant="caption" fontWeight={900} color="#0E2465">${totalExtras}</Typography>
          </Box>

          {/* Estado del pago de extras: se pagan antes de que el proveedor finalice */}
          {booking.pago && extrasEnProceso && (
            <Typography variant="caption" color="#A8740F" sx={{ display: 'block', mt: 0.5 }}>
              ⏳ Tu pago de extras está en proceso. Paga la referencia desde <strong>Datos de pago</strong>;
              en cuanto se confirme, el proveedor podrá finalizar el servicio.
            </Typography>
          )}
          {booking.pago && !extrasEnProceso && extrasRestante <= 0 && (
            <Typography variant="caption" color="#1B7A5A" sx={{ display: 'block', mt: 0.5 }}>
              ✓ Extras pagados. Cuando el servicio termine, comparte tu PIN al proveedor para finalizar.
            </Typography>
          )}
          {puedePagarExtras && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="#737791" sx={{ flex: 1, minWidth: 180 }}>
                Paga los extras para que el proveedor pueda finalizar el servicio.
              </Typography>
              <Button size="small" variant="contained" disabled={loading}
                onClick={() => setPagarExtrasOpen(true)}
                sx={{ bgcolor: '#59C39A', textTransform: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                  '&:hover': { bgcolor: '#4aad87' }, '&.Mui-disabled': { bgcolor: '#9DE1D5' } }}>
                Pagar extras (${extrasRestante})
              </Button>
            </Box>
          )}
          {booking.pago && !principalPagado && extrasRestante > 0 && !extrasEnProceso && (
            <Typography variant="caption" color="#737791" sx={{ display: 'block', mt: 0.5 }}>
              Podrás pagar los extras en cuanto el pago de tu reserva esté confirmado.
            </Typography>
          )}
        </Box>
      )}

      <Divider sx={{ mb: 1.5, borderColor: '#F1F5F9' }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box
          onClick={() => proveedor?._id && navigate(`/perfil/${proveedor._id}`)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            cursor: proveedor?._id ? 'pointer' : 'default',
            '&:hover .provider-name': proveedor?._id ? { textDecoration: 'underline' } : {}
          }}
        >
          <Typography variant="caption" color="#737791">Proveedor</Typography>
          <Avatar src={avatarSrc} sx={{ width: 28, height: 28, fontSize: '0.7rem' }}>
            {!avatarSrc && providerName.charAt(0)}
          </Avatar>
          <Typography className="provider-name" variant="body2" fontWeight="bold" color="#0E2465">{providerName}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {tieneTicket && (
            <Button size="small" variant="outlined"
              startIcon={<SupportAgentOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(`/user/tickets?booking=${booking._id}`)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', borderColor: '#7495F8', color: '#0E2465',
                '&:hover': { borderColor: '#5f83e8', bgcolor: '#EEF2FF' } }}>
              Ver ticket
            </Button>
          )}
          {esFinalizado ? (
            <>
              {facturaEmitida && <FacturaBotones factura={booking.factura} />}
              <Button size="small" variant="contained" disableElevation
                startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setDetalleOpen(true)}
                sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#0E2465', fontSize: '12px',
                  '&:hover': { bgcolor: '#0a1a4a' } }}>
                Ver detalles
              </Button>
            </>
          ) : (
            <>
              {booking.pago && (
                <Button size="small" variant="outlined"
                  startIcon={<ReceiptLongOutlinedIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setPagoOpen(true)}
                  sx={{ textTransform: 'none', borderRadius: '8px', borderColor: '#80A3FF', color: '#0E2465', fontSize: '12px',
                    '&:hover': { bgcolor: '#EEF2FF', borderColor: '#7495F8' } }}>
                  Datos de pago
                </Button>
              )}
              <Button size="small" variant="outlined"
                startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                disabled={!proveedor?._id}
                onClick={() => dispatch(startChat(proveedor._id, booking.servicio, 'service'))}
                sx={{ textTransform: 'none', borderRadius: '8px', borderColor: '#64D2B1', color: '#059669', fontSize: '12px',
                  '&:hover': { bgcolor: '#ECFDF5', borderColor: '#059669' } }}>
                Contactar proveedor
              </Button>
            </>
          )}
          {canModify && (
            <Button size="small" variant="contained" disabled={loading}
              startIcon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
              onClick={() => setModificarOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#FFD074', color: '#92400E', fontSize: '12px',
                '&:hover': { bgcolor: '#f0c35a' }, '&.Mui-disabled': { bgcolor: '#FFF3CD' } }}>
              Modificar fecha
            </Button>
          )}
          {canReport && (
            <Button size="small" variant="outlined" disabled={loading}
              startIcon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              onClick={() => setDisputaOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', borderColor: '#FF827A', color: '#B23B33', fontSize: '12px',
                '&:hover': { bgcolor: '#FFE0DD', borderColor: '#e06f68' } }}>
              Reportar problema
            </Button>
          )}
          {isPending && (
            <Button size="small" variant="contained" disabled={loading}
              startIcon={loading ? <CircularProgress size={12} color="inherit" /> : <CancelOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => setConfirmOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#FF827A', fontSize: '12px',
                '&:hover': { bgcolor: '#e06f68' }, '&.Mui-disabled': { bgcolor: '#fca5a5' } }}>
              Cancelar reserva
            </Button>
          )}
          {canCancelConfirmada && (
            <Button size="small" variant="contained" disabled={loading} disableElevation
              startIcon={<CancelOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => setCancelarConfirmadaOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#FF827A', color: 'white', fontSize: '12px',
                '&:hover': { bgcolor: '#e06f68' } }}>
              Cancelar reserva
            </Button>
          )}
        </Box>
      </Box>

      <ModificarModal
        open={modificarOpen}
        onClose={() => setModificarOpen(false)}
        loading={loading}
        booking={booking}
        directo={isPending}
        onConfirm={(id, fechaNueva, horaNueva) => {
          setModificarOpen(false);
          onModificar(id, fechaNueva, horaNueva);
        }}
      />

      {/* Dialog confirmación cancelación */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#FF827A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CancelOutlinedIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
          </Box>
          <Typography variant="h6" fontWeight="900" color="#0E2465" textAlign="center" sx={{ mb: 1 }}>
            ¿Cancelar esta reserva?
          </Typography>
          <Typography variant="body2" color="#737791" textAlign="center" sx={{ mb: 1 }}>
            <strong>{servicioNombre}</strong> — {formatDate(fecha)} a las {minutesToLabel(hora)}
          </Typography>
          <Typography variant="caption" color="#737791" display="block" textAlign="center" sx={{ mb: 3 }}>
            El proveedor será notificado de tu cancelación.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => setConfirmOpen(false)}
              sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: '#E2E8F0', color: '#737791' }}>
              Volver
            </Button>
            <Button fullWidth variant="contained" disabled={loading}
              onClick={() => { setConfirmOpen(false); onCancel(booking._id); }}
              sx={{ bgcolor: '#FF827A', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
                '&:hover': { bgcolor: '#e06f68' } }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Sí, cancelar'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <CancelarConfirmadaModal
        open={cancelConfirmadaOpen}
        onClose={() => setCancelarConfirmadaOpen(false)}
        loading={loading}
        booking={booking}
        esProveedor={false}
        penalizacion={penalizacion}
        onConfirm={() => {
          setCancelarConfirmadaOpen(false);
          onCancel(booking._id);
        }}
      />

      <PagoDialog open={pagoOpen} onClose={() => setPagoOpen(false)} bookingId={booking._id} />

      <ReporteServicioModal
        open={disputaOpen}
        onClose={() => setDisputaOpen(false)}
        loading={loading}
        esProveedor={false}
        booking={booking}
        onConfirm={async (payload) => {
          const ok = await onReportar(booking._id, payload);
          if (ok) setDisputaOpen(false);
        }}
      />

      <DetalleReservaModal
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        booking={booking}
        personaNombre={providerName}
        enRetencion={enRetencion}
        puedeReportar={dentroVentanaReporte(fecha)}
        onReportar={() => { setDetalleOpen(false); (enRetencion ? setDisputaOpen(true) : setAclaracionOpen(true)); }}
      />

      <ReporteServicioModal
        open={aclaracionOpen}
        onClose={() => setAclaracionOpen(false)}
        loading={loading}
        esProveedor={false}
        modo="aclaracion"
        booking={booking}
        onConfirm={async (payload) => {
          const ok = await onAclarar(booking._id, payload);
          if (ok) setAclaracionOpen(false);
        }}
      />

      {/* Confirmación del pago de extras (método heredado del pago principal) */}
      <Dialog open={pagarExtrasOpen} onClose={() => setPagarExtrasOpen(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#59C39A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
          </Box>
          <Typography variant="h6" fontWeight="900" color="#0E2465" textAlign="center" sx={{ mb: 1 }}>
            Pagar extras del servicio
          </Typography>
          <Typography variant="body2" color="#737791" textAlign="center" sx={{ mb: 2 }}>
            Total a pagar: <strong style={{ color: '#0E2465' }}>${extrasRestante} MXN</strong>
          </Typography>
          <Typography variant="caption" color="#737791" display="block" textAlign="center" sx={{ mb: 3 }}>
            {booking.pago?.metodo === 'card'
              ? 'Se hará el cargo a tu tarjeta guardada en este momento.'
              : booking.pago?.metodo === 'spei'
              ? 'Se generará una referencia SPEI; el pago se confirma al recibir tu transferencia.'
              : 'Se generará una referencia para pagar en tienda; el pago se confirma al pagarla.'}
            {' '}Una vez pagados, el proveedor podrá finalizar el servicio.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => setPagarExtrasOpen(false)}
              sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: '#E2E8F0', color: '#737791' }}>
              Volver
            </Button>
            <Button fullWidth variant="contained" disabled={loading}
              onClick={() => { setPagarExtrasOpen(false); onPagarExtras(booking._id); }}
              sx={{ bgcolor: '#59C39A', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
                '&:hover': { bgcolor: '#4aad87' } }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : booking.pago?.metodo === 'card' ? 'Pagar ahora' : 'Generar referencia'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const TABS = [
  { key: 'pendiente',  label: 'Solicitudes pendientes', statKey: 'pendiente' },
  { key: 'activo',     label: 'Servicios activos',      statKey: 'activo' },
  { key: 'proximo',    label: 'Servicios próximos',     statKey: 'proximo' },
  { key: 'finalizado', label: 'Finalizados',            statKey: 'finalizado' },
  { key: 'caducado',   label: 'Caducos',                statKey: 'caducado' },
  { key: 'cancelado',  label: 'Cancelados',             statKey: 'cancelado' }
];

// Pestañas históricas (pueden crecer sin límite) → muestran el filtro por periodo.
// Las de trabajo actual/futuro (pendiente/activo/próximo) no lo necesitan.
const TAB_CON_FILTRO = ['finalizado', 'caducado', 'cancelado'];

const HISTORIAL_ESTATUS = {
  pendiente: { label: 'Pendiente', bgcolor: '#FFD074', color: '#92400E' },
  activo:    { label: 'Activo',    bgcolor: '#9DE1D5', color: '#166534' },
  aceptado:  { label: 'Aceptado',  bgcolor: '#0E2465', color: 'white' },
  cobrado:   { label: 'Cobrado',   bgcolor: '#80A3FF', color: 'white' },
  cancelado: { label: 'Cancelado', bgcolor: '#FEE2E2', color: '#991B1B' },
  caducado:  { label: 'Caducado',  bgcolor: '#F3F4F6', color: '#6B7280' }
};

const getHistorialEstatusKey = (booking) => {
  const today = new Date().toISOString().split('T')[0];
  switch (booking.status) {
    case 'pendiente':  return 'pendiente';
    case 'confirmada': return booking.fecha === today ? 'activo' : 'aceptado';
    case 'completada': return 'cobrado';
    case 'cancelada':  return 'cancelado';
    case 'expirada':   return 'caducado';
    default:           return 'pendiente';
  }
};

// Convierte la duración de la reserva a la unidad de cobro del servicio (Hora / Día / Servicio)
const calcularUnidad = (booking) => {
  const mins = booking.duracionMinutos || 0;
  if (booking.tipoCosto === 'Hora') return Math.round((mins / 60) * 10) / 10;
  if (booking.tipoCosto === 'Día') return Math.round((mins / (60 * 24)) * 10) / 10;
  return 1;
};

const HistorialDetalleModal = ({ booking, vista, onClose }) => {
  if (!booking) return null;
  const otraParte = vista === 'proveedor' ? booking.cliente : booking.proveedor;
  const nombreOtraParte = otraParte ? `${otraParte.name} ${otraParte.apellido}` : 'NA';

  return (
    <Dialog open={!!booking} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} color="#0E2465" sx={{ mb: 2 }}>
          {booking.servicioNombre || 'Detalle del servicio'}
        </Typography>
        <Stack spacing={1.4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="#737791">{vista === 'proveedor' ? 'Cliente' : 'Proveedor'}</Typography>
            <Typography variant="body2" fontWeight={600} color="#374151">{nombreOtraParte}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="#737791">Fecha</Typography>
            <Typography variant="body2" fontWeight={600} color="#374151">{formatDate(booking.fecha)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="#737791">Hora</Typography>
            <Typography variant="body2" fontWeight={600} color="#374151">{minutesToLabel(booking.hora)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="#737791">Modalidad</Typography>
            <Typography variant="body2" fontWeight={600} color="#374151">{booking.modalidad}</Typography>
          </Box>
          {booking.nota && (
            <Box>
              <Typography variant="body2" color="#737791" sx={{ mb: 0.3 }}>Nota</Typography>
              <Typography variant="body2" color="#374151">{booking.nota}</Typography>
            </Box>
          )}
        </Stack>
        <Button fullWidth variant="contained" onClick={onClose}
          sx={{ mt: 3, bgcolor: '#0E2465', textTransform: 'none', borderRadius: '10px', py: 1.2, fontWeight: 700,
            '&:hover': { bgcolor: '#0a1b4d' } }}>
          Cerrar
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const HISTORIAL_COLUMNAS = [
  { label: 'Nombre',         flex: 1.4, align: 'left' },
  { label: '',               flex: 1.2, align: 'left' }, // Cliente / Proveedor — se etiqueta dinámicamente
  { label: 'Fecha',          flex: 1,   align: 'left' },
  { label: 'Estatus',        flex: 1,   align: 'center' },
  { label: 'Calificación',   flex: 1,   align: 'center' },
  { label: 'Costo x unidad', flex: 1,   align: 'center' },
  { label: 'Unidad',         flex: 0.7, align: 'center' },
  { label: 'Costo total',    flex: 1,   align: 'center' },
  { label: 'Detalles',       flex: 0.6, align: 'right' }
];

const HistorialRow = ({ booking, vista, isLast, onVer, onCalificar }) => {
  const estatus = HISTORIAL_ESTATUS[getHistorialEstatusKey(booking)];
  const unidad = calcularUnidad(booking);
  const costoUnidad = parseFloat(booking.costoServicio);
  const tieneCosto = !isNaN(costoUnidad);
  const otraParte = vista === 'proveedor' ? booking.cliente : booking.proveedor;
  const nombreOtraParte = otraParte ? `${otraParte.name} ${otraParte.apellido}` : 'NA';

  const calificacion = vista === 'proveedor'
    ? booking.calificacionProveedor
    : booking.calificacionCliente;
  const puedeCalificar = booking.status === 'completada' && !calificacion?.calificado;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.6, borderBottom: isLast ? 'none' : '1px solid #F1F5F9' }}>
      <Box sx={{ flex: 1.4, pr: 1 }}>
        <Typography variant="body2" fontWeight={600} color="#0E2465" noWrap>{booking.servicioNombre || 'NA'}</Typography>
      </Box>
      <Box sx={{ flex: 1.2, pr: 1 }}>
        <Typography variant="body2" color="#374151" noWrap>{nombreOtraParte}</Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="#374151" noWrap>{formatDate(booking.fecha)}</Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Chip label={estatus.label} size="small"
          sx={{ bgcolor: estatus.bgcolor, color: estatus.color, fontWeight: 700, borderRadius: '8px', minWidth: 84 }} />
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        {calificacion?.calificado ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Rating value={calificacion.rating} readOnly size="small" sx={{ color: '#0E2465', fontSize: '0.95rem' }} />
          </Box>
        ) : puedeCalificar ? (
          <Button size="small" onClick={() => onCalificar(booking)}
            sx={{ textTransform: 'none', color: '#7495f8', fontWeight: 700, fontSize: '0.75rem', minWidth: 0, px: 1,
              border: '1px solid #7495f8', borderRadius: '6px',
              '&:hover': { bgcolor: 'rgba(116,149,248,0.08)' } }}>
            Calificar
          </Button>
        ) : (
          <Typography variant="body2" color="#C4C9D4">—</Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography variant="body2" color="#374151">{tieneCosto ? `$${costoUnidad.toFixed(2)}` : 'NA'}</Typography>
      </Box>
      <Box sx={{ flex: 0.7, textAlign: 'center' }}>
        <Typography variant="body2" color="#374151">{unidad}</Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography variant="body2" fontWeight={700} color="#374151">
          {tieneCosto ? `$${(costoUnidad * unidad).toFixed(2)}` : 'NA'}
        </Typography>
      </Box>
      <Box sx={{ flex: 0.6, textAlign: 'right' }}>
        <Button size="small" onClick={onVer}
          sx={{ textTransform: 'none', color: '#0E2465', fontWeight: 700, fontSize: '0.8rem', minWidth: 0, p: 0,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
          Ver
        </Button>
      </Box>
    </Box>
  );
};

const HistorialTable = ({ bookings, vista, onCalificar, actionLoading }) => {
  const [detalle, setDetalle] = useState(null);
  const [calificarBooking, setCalificarBooking] = useState(null);
  const otraParteLabel = vista === 'proveedor' ? 'Cliente' : 'Proveedor';

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.6, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
        {HISTORIAL_COLUMNAS.map((c, i) => (
          <Box key={i} sx={{ flex: c.flex, textAlign: c.align }}>
            <Typography variant="caption" fontWeight={700} color="#374151" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
              {c.label || otraParteLabel}
            </Typography>
          </Box>
        ))}
      </Box>
      {bookings.map((b, i) => (
        <HistorialRow
          key={b._id}
          booking={b}
          vista={vista}
          isLast={i === bookings.length - 1}
          onVer={() => setDetalle(b)}
          onCalificar={(booking) => setCalificarBooking(booking)}
        />
      ))}
      <HistorialDetalleModal booking={detalle} vista={vista} onClose={() => setDetalle(null)} />
      <CalificarModal
        open={!!calificarBooking}
        onClose={() => setCalificarBooking(null)}
        loading={actionLoading}
        esProveedor={vista === 'proveedor'}
        onConfirm={(rating, comentario) => {
          onCalificar(calificarBooking._id, rating, comentario);
          setCalificarBooking(null);
        }}
      />
    </Box>
  );
};

// ---- Filtros del Historial ----------------------------------------------
// Presets de tiempo (lo más visible) + estados seleccionables. Por defecto se
// muestran los últimos 3 meses y se excluyen canceladas/caducadas.
const HISTORIAL_TIEMPO_OPCIONES = [
  { key: '3m',    label: 'Últimos 3 meses', meses: 3 },
  { key: '6m',    label: '6 meses',         meses: 6 },
  { key: '1a',    label: '1 año',           meses: 12 },
  { key: 'rango', label: 'Personalizado',   meses: null }
];

const HISTORIAL_ESTADO_OPCIONES = [
  { key: 'pendiente',  label: 'Pendiente',  bgcolor: '#FFD074', color: '#92400E' },
  { key: 'confirmada', label: 'Confirmada', bgcolor: '#9DE1D5', color: '#166534' },
  { key: 'completada', label: 'Completada', bgcolor: '#80A3FF', color: 'white' },
  { key: 'cancelada',  label: 'Cancelada',  bgcolor: '#FEE2E2', color: '#991B1B' },
  { key: 'expirada',   label: 'Caducada',   bgcolor: '#F3F4F6', color: '#6B7280' }
];

const HISTORIAL_ESTADOS_DEFAULT = ['pendiente', 'confirmada', 'completada'];

const isoRestarMeses = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().split('T')[0];
};

const fechaLarga = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

// Texto que le dice al usuario qué rango está viendo (lo más visible)
const textoRangoHistorial = (tiempo, desde, hasta) => {
  switch (tiempo) {
    case '3m': return 'Mostrando reservas de los últimos 3 meses';
    case '6m': return 'Mostrando reservas de los últimos 6 meses';
    case '1a': return 'Mostrando reservas del último año';
    case 'rango':
      if (desde && hasta) return `Mostrando del ${fechaLarga(desde)} al ${fechaLarga(hasta)}`;
      if (desde) return `Mostrando desde el ${fechaLarga(desde)}`;
      if (hasta) return `Mostrando hasta el ${fechaLarga(hasta)}`;
      return 'Elige un rango de fechas';
    default: return '';
  }
};

// Ventana en la que aún se puede reportar/aclarar un servicio: últimos 3 meses (por
// fecha del servicio). Los más viejos ya no muestran el botón de reportar.
const dentroVentanaReporte = (fecha) => !!fecha && fecha >= isoRestarMeses(3);

const HistorialFiltros = ({ titulo = 'Filtrar historial', mostrarEstatus = true, opciones = HISTORIAL_TIEMPO_OPCIONES, tiempo, onTiempo, desde, hasta, onDesde, onHasta, estados, onToggleEstado }) => {
  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: '1px solid #E2E8F0', p: { xs: 2, md: 2.5 }, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <FilterAltOutlinedIcon sx={{ color: '#0E2465', fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight={700} color="#0E2465">{titulo}</Typography>
      </Stack>

      {/* Periodo — el filtro más visible */}
      <Typography variant="caption" fontWeight={700} color="#64748B"
        sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.4, display: 'block', mb: 1 }}>
        Periodo
      </Typography>
      <ToggleButtonGroup
        value={tiempo}
        exclusive
        onChange={(_, val) => { if (val) onTiempo(val); }}
        sx={{
          flexWrap: 'wrap', gap: 1,
          '& .MuiToggleButtonGroup-grouped': {
            border: '1px solid #E2E8F0 !important', borderRadius: '999px !important',
            px: 2, py: 0.6, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
            color: '#475569', bgcolor: '#F8FAFC',
            '&:hover': { bgcolor: '#EEF2F7' },
            '&.Mui-selected': {
              bgcolor: '#0E2465', color: 'white', borderColor: '#0E2465 !important',
              '&:hover': { bgcolor: '#0a1b4d' }
            }
          }
        }}
      >
        {opciones.map(op => (
          <ToggleButton key={op.key} value={op.key}>{op.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Rango personalizado */}
      {tiempo === 'rango' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            type="date" label="Desde" size="small" value={desde}
            onChange={(e) => onDesde(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
          <TextField
            type="date" label="Hasta" size="small" value={hasta}
            onChange={(e) => onHasta(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
        </Stack>
      )}

      {mostrarEstatus && (
        <>
          <Divider sx={{ my: 2 }} />

          {/* Estatus */}
          <Typography variant="caption" fontWeight={700} color="#64748B"
            sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.4, display: 'block', mb: 1 }}>
            Estatus
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {HISTORIAL_ESTADO_OPCIONES.map(op => {
              const activo = estados.includes(op.key);
              return (
                <Chip
                  key={op.key}
                  label={op.label}
                  onClick={() => onToggleEstado(op.key)}
                  variant={activo ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 700, borderRadius: '8px', cursor: 'pointer',
                    bgcolor: activo ? op.bgcolor : 'transparent',
                    color: activo ? op.color : '#94A3B8',
                    border: activo ? '1px solid transparent' : '1px solid #E2E8F0',
                    '&:hover': { bgcolor: activo ? op.bgcolor : '#F8FAFC', opacity: activo ? 0.9 : 1 }
                  }}
                />
              );
            })}
          </Stack>
        </>
      )}

      {/* Info de rango — lo más visible: qué está viendo */}
      <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 2 }}>
        <EventOutlinedIcon sx={{ color: '#7495F8', fontSize: 18 }} />
        <Typography variant="caption" fontWeight={600} color="#475569">
          {textoRangoHistorial(tiempo, desde, hasta)}
        </Typography>
      </Stack>
    </Box>
  );
};

const CalendarDetalleModal = ({ booking, onClose }) => {
  if (!booking) return null;
  const estatus = STATUS_COLOR[booking.status] || STATUS_COLOR.pendiente;
  const clientName = booking.cliente ? `${booking.cliente.name} ${booking.cliente.apellido}` : 'Cliente';

  return (
    <Dialog open={!!booking} onClose={onClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="#0E2465">
            {booking.servicioNombre || 'Servicio'}
          </Typography>
          <Chip label={estatus.label} size="small"
            sx={{ bgcolor: estatus.bgcolor, color: estatus.color, fontWeight: 'bold', borderRadius: 1 }} />
        </Box>
        <Stack spacing={1.2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="#737791">Cliente</Typography>
            <Typography variant="body2" fontWeight="bold" color="#0E2465">{clientName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="#737791">Fecha</Typography>
            <Typography variant="body2" color="#0E2465">{formatDate(booking.fecha)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="#737791">Hora</Typography>
            <Typography variant="body2" color="#0E2465">{minutesToLabel(booking.hora)} — {minutesToLabel(booking.hora, booking.duracionMinutos)}</Typography>
          </Box>
        </Stack>
        <Button fullWidth variant="contained" onClick={onClose}
          sx={{ mt: 3, bgcolor: '#0E2465', textTransform: 'none', borderRadius: '10px', py: 1.2, fontWeight: 700, '&:hover': { bgcolor: '#0a1b4d' } }}>
          Cerrar
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const ProviderCalendar = ({ bookings, month, onPrevMonth, onNextMonth }) => {
  const [detalle, setDetalle] = useState(null);

  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const monthLabel = `${MONTH_NAMES[monthIndex]} ${year}`;
  const firstDayOfMonth = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const today = new Date();
  const isToday = (dayNum) => dayNum === today.getDate() && monthIndex === today.getMonth() && year === today.getFullYear();

  const bookingsByDay = {};
  bookings.forEach(b => {
    const dayNum = Number((b.fecha || '').split('-')[2]);
    if (!dayNum) return;
    (bookingsByDay[dayNum] = bookingsByDay[dayNum] || []).push(b);
  });

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: '1px solid #E2E8F0', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Button variant="contained" size="small" disableElevation
          sx={{ bgcolor: '#0E2465', color: 'white', textTransform: 'none', borderRadius: '8px', fontSize: '12px', px: 2.5, '&:hover': { bgcolor: '#0E2465' } }}>
          Mes
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton size="small" onClick={onPrevMonth} sx={{ bgcolor: '#F1F5F9' }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" fontWeight="bold" color="#0E2465" sx={{ minWidth: 120, textAlign: 'center' }}>
            {monthLabel}
          </Typography>
          <IconButton size="small" onClick={onNextMonth} sx={{ bgcolor: '#F1F5F9', border: '1px solid #7495f8', color: '#7495f8' }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={1}>
        {DAY_NAMES.map(d => (
          <Grid size={1.7} key={d} sx={{ textAlign: 'center', pb: 0.5 }}>
            <Typography variant="caption" color="#737791" fontWeight="bold">{d}</Typography>
          </Grid>
        ))}

        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
          <Grid size={1.7} key={`empty-${i}`}>
            <Box sx={{ minHeight: 96, border: '1px solid #F8FAFC', borderRadius: '8px' }} />
          </Grid>
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1;
          const dayBookings = bookingsByDay[dayNum] || [];
          return (
            <Grid size={1.7} key={dayNum}>
              <Box sx={{
                minHeight: 96, borderRadius: '8px', p: 0.8,
                border: isToday(dayNum) ? '1px solid #7495F8' : '1px solid #F1F5F9',
                bgcolor: isToday(dayNum) ? 'rgba(116,149,248,0.05)' : 'transparent'
              }}>
                <Typography variant="caption" color="#737791" sx={{ pl: 0.5 }}>{dayNum}</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.3 }}>
                  {dayBookings.slice(0, 2).map(b => {
                    const estatus = STATUS_COLOR[b.status] || STATUS_COLOR.pendiente;
                    return (
                      <Box key={b._id} onClick={() => setDetalle(b)}
                        sx={{ bgcolor: estatus.bgcolor, color: estatus.color, borderRadius: '6px', px: 0.8, py: 0.4, cursor: 'pointer' }}>
                        <Typography sx={{ fontSize: '10px', fontWeight: 'bold', lineHeight: 1.3 }}>
                          {minutesToLabel(b.hora)}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {b.servicioNombre}
                        </Typography>
                      </Box>
                    );
                  })}
                  {dayBookings.length > 2 && (
                    <Typography sx={{ fontSize: '10px', color: '#9CA3AF', pl: 0.5 }}>
                      +{dayBookings.length - 2} más
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <CalendarDetalleModal booking={detalle} onClose={() => setDetalle(null)} />
    </Box>
  );
};

const STATS_EMPTY = { pendiente: 0, activo: 0, proximo: 0, finalizado: 0, caducado: 0, cancelado: 0, cobrar: 0, recibir: 0, totalMes: 0 };

const ProviderBookings = () => {
  // Por defecto se entra como cliente; las notificaciones del proveedor llegan con ?vista=proveedor
  const [searchParams] = useSearchParams();
  const [vista, setVista] = useState(searchParams.get('vista') === 'proveedor' ? 'proveedor' : 'cliente');
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(STATS_EMPTY);
  const [bookings, setBookings] = useState([]);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const [historial, setHistorial] = useState([]);
  const [historialPages, setHistorialPages] = useState(1);
  const [historialPage, setHistorialPage] = useState(1);
  const [historialLoading, setHistorialLoading] = useState(true);

  // Filtros del historial: periodo (default últimos 3 meses) y estados
  // (default sin canceladas/caducadas). El usuario puede ampliarlos.
  const [histTiempo, setHistTiempo] = useState('3m');
  const [histDesde, setHistDesde] = useState('');
  const [histHasta, setHistHasta] = useState('');
  const [histEstados, setHistEstados] = useState(HISTORIAL_ESTADOS_DEFAULT);

  // Traduce los filtros de la UI a los params que espera el backend
  const filtrosHistorial = useMemo(() => {
    const f = { estados: histEstados };
    if (histTiempo === 'rango') {
      if (histDesde) f.desde = histDesde;
      if (histHasta) f.hasta = histHasta;
    } else {
      const meses = histTiempo === '6m' ? 6 : histTiempo === '1a' ? 12 : 3;
      f.desde = isoRestarMeses(meses);
    }
    return f;
  }, [histTiempo, histDesde, histHasta, histEstados]);

  const toggleHistEstado = (key) => {
    setHistEstados(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  // Filtro por periodo de las pestañas históricas (Finalizados/Caducos/Cancelados).
  // Por defecto últimos 3 meses; con 6m/1año/Personalizado se ven los más viejos.
  const [tabTiempo, setTabTiempo] = useState('3m');
  const [tabDesde, setTabDesde] = useState('');
  const [tabHasta, setTabHasta] = useState('');

  const tabActualUsaFiltro = TAB_CON_FILTRO.includes(TABS[tab].key);

  const filtrosTab = useMemo(() => {
    const f = {};
    if (tabTiempo === 'rango') {
      if (tabDesde) f.desde = tabDesde;
      if (tabHasta) f.hasta = tabHasta;
    } else {
      const meses = tabTiempo === '6m' ? 6 : tabTiempo === '1a' ? 12 : 3;
      f.desde = isoRestarMeses(meses);
    }
    return f;
  }, [tabTiempo, tabDesde, tabHasta]);

  // Filtros que realmente se envían para la pestaña activa (vacío si no aplica)
  const filtrosTabActivos = tabActualUsaFiltro ? filtrosTab : {};

  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [calendarBookings, setCalendarBookings] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);

  // Reservas para las que el usuario tiene un ticket propio → muestran "Ver ticket".
  // Se basa en los tickets reales del usuario (reportes Y aclaraciones), no en el
  // campo booking.disputa (que también incluye disputas viejas sin ticket).
  const [ticketBookingIds, setTicketBookingIds] = useState(() => new Set());
  useEffect(() => {
    getMisTickets()
      .then(ts => setTicketBookingIds(new Set((ts || []).map(t => t.booking?._id).filter(Boolean))))
      .catch(() => {});
  }, []);

  const fetchStats = useCallback(async (v) => {
    const data = v === 'proveedor' ? await getProviderStats() : await getClientStats();
    setStats(data || STATS_EMPTY);
  }, []);

  const fetchBookings = useCallback(async (v, tabKey, p, filtros = {}) => {
    setLoading(true);
    const data = v === 'proveedor'
      ? await getProviderBookings(tabKey, p, filtros)
      : await getClientBookings(tabKey, p, filtros);
    setBookings(data.bookings || []);
    setPages(data.pages || 1);
    setLoading(false);
  }, []);

  const fetchHistorial = useCallback(async (v, p, filtros) => {
    setHistorialLoading(true);
    const data = v === 'proveedor'
      ? await getProviderBookings('historial', p, filtros)
      : await getClientBookings('historial', p, filtros);
    setHistorial(data.bookings || []);
    setHistorialPages(data.pages || 1);
    setHistorialLoading(false);
  }, []);

  // Si llega ?vista= estando ya en la página (p. ej. desde una notificación), sincroniza
  useEffect(() => {
    const v = searchParams.get('vista');
    if (v === 'proveedor' || v === 'cliente') {
      setVista(v);
      setTab(0);
      setPage(1);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchStats(vista);
  }, [vista, fetchStats]);

  useEffect(() => {
    setPage(1);
    const usa = TAB_CON_FILTRO.includes(TABS[tab].key);
    fetchBookings(vista, TABS[tab].key, 1, usa ? filtrosTab : {});
  }, [vista, tab, filtrosTab, fetchBookings]);

  useEffect(() => {
    setHistorialPage(1);
    fetchHistorial(vista, 1, filtrosHistorial);
  }, [vista, filtrosHistorial, fetchHistorial]);

  // Al cambiar de página subimos al inicio de la sección (con margen para el header
  // fijo) para que la nueva página no quede "perdida" abajo si tiene pocos elementos.
  const tabsRef = useRef(null);
  const historialRef = useRef(null);
  const scrollAInicio = (ref) => {
    const el = ref.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const handleHistorialPageChange = (_, val) => {
    setHistorialPage(val);
    fetchHistorial(vista, val, filtrosHistorial);
    scrollAInicio(historialRef);
  };

  const fetchCalendar = useCallback(async (date) => {
    setCalendarLoading(true);
    const data = await getProviderCalendar(date.getMonth(), date.getFullYear());
    setCalendarBookings(data || []);
    setCalendarLoading(false);
  }, []);

  useEffect(() => {
    if (vista !== 'proveedor') return;
    fetchCalendar(calendarMonth);
  }, [vista, calendarMonth, fetchCalendar]);

  const handlePrevCalendarMonth = () => {
    setCalendarMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  };
  const handleNextCalendarMonth = () => {
    setCalendarMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  };

  const handleVista = (v) => {
    if (v === vista) return;
    setVista(v);
    setTab(0);
    setPage(1);
  };

  const handlePageChange = (_, val) => {
    setPage(val);
    fetchBookings(vista, TABS[tab].key, val, filtrosTabActivos);
    scrollAInicio(tabsRef);
  };

  const handleAction = async (bookingId, status, motivo = '') => {
    setActionLoading(true);
    const result = await updateBookingStatus(bookingId, status, motivo);
    setActionLoading(false);
    if (result?.success) {
      const msg = status === 'confirmada' ? 'Reserva confirmada y cliente notificado.' : 'Reserva declinada. El cliente fue notificado.';
      setToast({ open: true, msg, severity: status === 'confirmada' ? 'success' : 'warning' });
      fetchStats(vista);
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      fetchHistorial(vista, historialPage, filtrosHistorial);
      fetchCalendar(calendarMonth);
    } else {
      // El backend puede devolver un motivo específico (p. ej. cobro de tarjeta rechazado)
      setToast({ open: true, msg: result?.msg || 'Error al actualizar. Intenta de nuevo.', severity: 'error' });
    }
  };

  const handleSolicitarCambio = async (bookingId, fechaNueva, horaNueva) => {
    setActionLoading(true);
    const result = await solicitarCambio(bookingId, fechaNueva, horaNueva);
    setActionLoading(false);
    if (result?.success) {
      const msg = result.aplicado
        ? 'Fecha actualizada. El proveedor fue notificado del cambio.'
        : vista === 'proveedor'
        ? 'Propuesta enviada. El cliente decidirá el cambio.'
        : 'Solicitud enviada. El proveedor decidirá el cambio.';
      setToast({ open: true, msg, severity: result.aplicado ? 'success' : 'info' });
      if (result.aplicado) {
        fetchStats(vista);
        fetchHistorial(vista, historialPage, filtrosHistorial);
      }
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
    } else {
      setToast({ open: true, msg: result?.msg || 'Error al enviar la solicitud. Intenta de nuevo.', severity: 'error' });
    }
  };

  const handleResponderCambio = async (bookingId, aceptar) => {
    setActionLoading(true);
    const result = await responderCambio(bookingId, aceptar);
    setActionLoading(false);
    if (result?.success) {
      // confirmada llega cuando el cliente acepta la propuesta del proveedor en una
      // reserva pendiente: true = se confirmó en automático, false = falló el cobro
      let msg = aceptar ? 'Cambio aceptado. La reserva se actualizó.' : 'Cambio rechazado. La reserva mantiene su fecha original.';
      let severity = aceptar ? 'success' : 'warning';
      if (result.confirmada === true) {
        msg = 'Aceptaste la nueva fecha y la reserva quedó confirmada.';
      } else if (result.confirmada === false) {
        msg = `Aceptaste la nueva fecha, pero la reserva sigue pendiente: ${result.msgPago || 'no se pudo procesar el pago.'}`;
        severity = 'warning';
      }
      setToast({ open: true, msg, severity });
      fetchStats(vista);
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      fetchHistorial(vista, historialPage, filtrosHistorial);
      fetchCalendar(calendarMonth);
    } else {
      setToast({ open: true, msg: result?.msg || 'Error al responder. Intenta de nuevo.', severity: 'error' });
    }
  };

  const handleComplete = async (bookingId, pin = '') => {
    setActionLoading(true);
    const result = await completeBooking(bookingId, pin);
    setActionLoading(false);
    if (result?.success) {
      // El pago queda retenido (ventana de protección) y se libera en el corte semanal
      let msg = 'Servicio marcado como completado. Se reflejó en tus estadísticas.';
      let severity = 'success';
      if (result.pago === 'retenido') {
        msg = 'Servicio completado. Tu pago queda retenido ~1 semana (por si hay reportes) y se libera en el corte semanal si todo está en orden.';
      }
      setToast({ open: true, msg, severity });
      fetchStats(vista);
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      fetchHistorial(vista, historialPage, filtrosHistorial);
      fetchCalendar(calendarMonth);
    } else {
      // p. ej. PIN incorrecto
      setToast({ open: true, msg: result?.msg || 'Error al completar el servicio. Intenta de nuevo.', severity: 'error' });
    }
  };

  const handleAddExtra = async (bookingId, concepto, monto) => {
    setActionLoading(true);
    const result = await addExtra(bookingId, concepto, monto);
    setActionLoading(false);
    if (result?.success) {
      setToast({ open: true, msg: 'Extra agregado. El cliente ya puede verlo en su reserva.', severity: 'success' });
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
    } else {
      setToast({ open: true, msg: result?.msg || 'No se pudo agregar el extra.', severity: 'error' });
    }
  };

  const handleReportarProblema = async (bookingId, payload) => {
    setActionLoading(true);
    const result = await reportarProblema(bookingId, payload);
    setActionLoading(false);
    if (result?.success) {
      setToast({ open: true, msg: 'Reporte enviado. El pago queda congelado mientras revisamos el caso; te avisaremos la resolución.', severity: 'info' });
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      return true;
    }
    setToast({ open: true, msg: result?.msg || 'No se pudo enviar el reporte. Intenta de nuevo.', severity: 'error' });
    return false;
  };

  const handleAclaracion = async (bookingId, payload) => {
    setActionLoading(true);
    const result = await crearAclaracion(bookingId, payload);
    setActionLoading(false);
    if (result?.success) {
      setToast({ open: true, msg: 'Aclaración enviada. Nuestro equipo de soporte revisará el caso y te contactará.', severity: 'info' });
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      return true;
    }
    setToast({ open: true, msg: result?.msg || 'No se pudo enviar la aclaración. Intenta de nuevo.', severity: 'error' });
    return false;
  };

  const handlePagarExtras = async (bookingId) => {
    setActionLoading(true);
    const result = await pagarExtras(bookingId);
    setActionLoading(false);
    if (result?.success) {
      const msg = result.resultado === 'cobrado'
        ? 'Extras pagados. Cuando el servicio termine, comparte tu PIN al proveedor para finalizar.'
        : 'Se generó tu referencia de pago. Revisa "Datos de pago" para pagarla.';
      setToast({ open: true, msg, severity: result.resultado === 'cobrado' ? 'success' : 'info' });
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
    } else {
      setToast({ open: true, msg: result?.msg || 'No se pudo procesar el pago de los extras.', severity: 'error' });
    }
  };

  const handleRemoveExtra = async (bookingId, extraId) => {
    setActionLoading(true);
    const result = await removeExtra(bookingId, extraId);
    setActionLoading(false);
    if (result?.success) {
      setToast({ open: true, msg: 'Extra eliminado.', severity: 'info' });
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
    } else {
      setToast({ open: true, msg: result?.msg || 'No se pudo quitar el extra.', severity: 'error' });
    }
  };

  const handleCalificar = async (bookingId, rating, comentario) => {
    setActionLoading(true);
    const result = await submitReview(bookingId, rating, comentario);
    setActionLoading(false);
    if (result?.success) {
      setToast({ open: true, msg: '¡Gracias por tu calificación!', severity: 'success' });
      fetchHistorial(vista, historialPage, filtrosHistorial);
    } else {
      setToast({ open: true, msg: 'Error al enviar la calificación. Intenta de nuevo.', severity: 'error' });
    }
  };

  const handleClientCancel = async (bookingId) => {
    setActionLoading(true);
    const result = await cancelClientBooking(bookingId);
    setActionLoading(false);
    if (result?.success) {
      const msg = result.penalizacionPct > 0
        ? `Reserva cancelada con penalización del ${result.penalizacionPct}%. El resto de tu pago se reembolsará.`
        : 'Reserva cancelada. El proveedor fue notificado.';
      setToast({ open: true, msg, severity: 'warning' });
      fetchStats(vista);
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      fetchHistorial(vista, historialPage, filtrosHistorial);
    } else {
      setToast({ open: true, msg: result?.msg || 'Error al cancelar. Intenta de nuevo.', severity: 'error' });
    }
  };

  return (
    <Box className="main-page">
      {/* Header */}
      <Box sx={{ bgcolor: '#7495f8', color: 'white', borderRadius: 2, px: 4, py: 5, mb: 3, position: 'relative' }}>
        <Typography variant="h5">
          {vista === 'proveedor' ? 'Reservas como proveedor' : 'Mis servicios como usuario'}
        </Typography>
        <Typography mt={2} variant="body1" color="primary">
          <small>{vista === 'proveedor' ? 'Gestiona las solicitudes de tus clientes' : 'Revisa los servicios que has contratado'}</small>
        </Typography>

        {/* Toggle cliente / proveedor */}
        <Box sx={{ position: 'absolute', top: 24, right: 24, display: 'flex', bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '10px', p: 0.5 }}>
          <Button
            size="small"
            startIcon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleVista('cliente')}
            sx={{
              textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700, px: 1.5,
              color: vista === 'cliente' ? '#0E2465' : 'white',
              bgcolor: vista === 'cliente' ? 'white' : 'transparent',
              '&:hover': { bgcolor: vista === 'cliente' ? 'white' : 'rgba(255,255,255,0.2)' }
            }}>
            Cliente
          </Button>
          <Button
            size="small"
            startIcon={<WorkOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleVista('proveedor')}
            sx={{
              textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700, px: 1.5,
              color: vista === 'proveedor' ? '#0E2465' : 'white',
              bgcolor: vista === 'proveedor' ? 'white' : 'transparent',
              '&:hover': { bgcolor: vista === 'proveedor' ? 'white' : 'rgba(255,255,255,0.2)' }
            }}>
            Proveedor
          </Button>
        </Box>
      </Box>

      <Box>
        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Servicios contratados */}
          <Grid item size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="#0E2465" sx={{ mb: 0.3 }}>
              Servicios contratados
            </Typography>
            <Typography variant="caption" color="#9CA3AF" sx={{ display: 'block', mb: 1.5 }}>
              Solicitudes actualizadas
            </Typography>
            <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: '1px solid #E2E8F0', p: 2.5, display: 'flex' }}>
              {CONTRATADOS_STATS.map((s, i) => (
                <Box key={s.key} sx={{
                  flex: 1, textAlign: 'center', px: 1,
                  borderRight: i < CONTRATADOS_STATS.length - 1 ? '1px solid #E2E8F0' : 'none'
                }}>
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    color="#374151"
                    sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }, whiteSpace: 'nowrap' }}
                  >
                    {formatCompactNumber(stats[s.key])}
                  </Typography>
                  <Box sx={{ height: 4, bgcolor: s.color, borderRadius: 2, mt: 0.8, mb: 0.6 }} />
                  <Typography variant="caption" color="#9CA3AF" sx={{ fontSize: '0.72rem' }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Pagos */}
          <Grid item size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="#0E2465" sx={{ mb: 0.3 }}>
              Pagos
            </Typography>
            <Typography variant="caption" color="#9CA3AF" sx={{ display: 'block', mb: 1.5 }}>
              Próximamente disponible
            </Typography>
            <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: '1px solid #E2E8F0', p: 2.5, display: 'flex' }}>
              {getPagosStats(stats).map((s, i) => (
                <Box key={s.key} sx={{
                  flex: 1, textAlign: 'center', px: 1,
                  borderRight: i < getPagosStats(stats).length - 1 ? '1px solid #E2E8F0' : 'none'
                }}>
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    color="#374151"
                    sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }, whiteSpace: 'nowrap' }}
                  >
                    {s.esMoneda ? formatCompactCurrency(s.value) : formatCompactNumber(s.value)}
                  </Typography>
                  <Box sx={{ height: 4, bgcolor: s.color, borderRadius: 2, mt: 0.8, mb: 0.6 }} />
                  <Typography variant="caption" color="#9CA3AF" sx={{ fontSize: '0.72rem' }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box ref={tabsRef} sx={{ bgcolor: 'white', borderRadius: '20px', border: '1px solid #F1F5F9', overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="fullWidth"
            sx={{
              borderBottom: '1px solid #F1F5F9',
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, color: '#737791', fontSize: '0.85rem' },
              '& .Mui-selected': { color: '#0E2465' },
              '& .MuiTabs-indicator': { bgcolor: '#0E2465', height: 3, borderRadius: '3px 3px 0 0' }
            }}
          >
            {TABS.map((t, i) => (
              <Tab key={t.key} label={`${t.label} (${stats[t.statKey] ?? 0})`} />
            ))}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {tabActualUsaFiltro && (
              <HistorialFiltros
                titulo="Filtrar por periodo"
                mostrarEstatus={false}
                tiempo={tabTiempo}
                onTiempo={setTabTiempo}
                desde={tabDesde}
                hasta={tabHasta}
                onDesde={setTabDesde}
                onHasta={setTabHasta}
              />
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : bookings.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="#737791">
                  {tabActualUsaFiltro
                    ? 'No hay reservas en este periodo. Prueba ampliarlo o usar un rango personalizado.'
                    : 'No hay reservas en esta categoría.'}
                </Typography>
              </Box>
            ) : (
              <>
                {bookings.map(b => (
                  vista === 'proveedor'
                    ? <BookingCard
                        key={b._id}
                        booking={b}
                        tieneTicket={ticketBookingIds.has(b._id)}
                        loading={actionLoading}
                        onAccept={(id) => handleAction(id, 'confirmada')}
                        onDecline={(id, motivo) => handleAction(id, 'cancelada', motivo)}
                        onComplete={handleComplete}
                        onModificar={handleSolicitarCambio}
                        onResponderCambio={handleResponderCambio}
                        onAddExtra={handleAddExtra}
                        onRemoveExtra={handleRemoveExtra}
                        onReportar={handleReportarProblema}
                        onAclarar={handleAclaracion}
                      />
                    : <ClientBookingCard
                        key={b._id}
                        booking={b}
                        tieneTicket={ticketBookingIds.has(b._id)}
                        onCancel={handleClientCancel}
                        onModificar={handleSolicitarCambio}
                        onResponderCambio={handleResponderCambio}
                        onPagarExtras={handlePagarExtras}
                        onReportar={handleReportarProblema}
                        onAclarar={handleAclaracion}
                        loading={actionLoading}
                      />
                ))}

                {pages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={pages}
                      page={page}
                      onChange={handlePageChange}
                      sx={{
                        '& .MuiPaginationItem-root': { borderRadius: '8px' },
                        '& .Mui-selected': { bgcolor: '#0E2465 !important', color: 'white' }
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>

        {vista === 'proveedor' && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="#0E2465" sx={{ mb: 0.3 }}>
              Calendario
            </Typography>
            <Typography variant="caption" color="#9CA3AF" sx={{ display: 'block', mb: 1.5 }}>
              Servicios pactados (confirmados) y solicitudes pendientes por aceptar
            </Typography>

            {calendarLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ProviderCalendar
                bookings={calendarBookings}
                month={calendarMonth}
                onPrevMonth={handlePrevCalendarMonth}
                onNextMonth={handleNextCalendarMonth}
              />
            )}
          </Box>
        )}

        {/* Historial */}
        <Box ref={historialRef} sx={{ mt: 4 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="#0E2465" sx={{ mb: 0.3 }}>
            Historial
          </Typography>
          <Typography variant="caption" color="#9CA3AF" sx={{ display: 'block', mb: 1.5 }}>
            {vista === 'proveedor'
              ? 'Reservas recibidas. Por defecto se muestran los últimos 3 meses (sin canceladas ni caducadas).'
              : 'Servicios que has contratado. Por defecto se muestran los últimos 3 meses (sin canceladas ni caducadas).'}
          </Typography>

          <HistorialFiltros
            tiempo={histTiempo}
            onTiempo={setHistTiempo}
            desde={histDesde}
            hasta={histHasta}
            onDesde={setHistDesde}
            onHasta={setHistHasta}
            estados={histEstados}
            onToggleEstado={toggleHistEstado}
          />

          {historialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : historial.length === 0 ? (
            <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: '1px solid #E2E8F0', textAlign: 'center', py: 6 }}>
              <Typography variant="body2" color="#737791">
                No hay reservas con los filtros seleccionados.
              </Typography>
              <Typography variant="caption" color="#9CA3AF" sx={{ display: 'block', mt: 0.5 }}>
                Prueba ampliar el periodo o incluir más estatus.
              </Typography>
            </Box>
          ) : (
            <>
              <HistorialTable bookings={historial} vista={vista} onCalificar={handleCalificar} actionLoading={actionLoading} />

              {historialPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={historialPages}
                    page={historialPage}
                    onChange={handleHistorialPageChange}
                    sx={{
                      '& .MuiPaginationItem-root': { borderRadius: '8px' },
                      '& .Mui-selected': { bgcolor: '#0E2465 !important', color: 'white' }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))}
          sx={{ borderRadius: '12px', fontWeight: 600 }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderBookings;
